// Code generated by balancegen from the data layer (data/*/*.toml). DO NOT EDIT.
// Regenerate with scripts/gen-data.sh.
//
// The compile-time lane (R310): the values the client may hold
// without waiting for the wire. Game tables are NOT here — they
// arrive in the Hello catalog, and this file must never grow one.

import { LOCALE } from './strings.load';

/** Must match hub.ProtocolVersion — generated from the same TOML. */
export const PROTOCOL = 49;

/** The number of fill steps a stocked deposit is drawn in, and the
 * granularity at which the server reports a stock change
 * (ui-map-interface.md §6.2, R475). Must match world.DepositLevels —
 * generated from the same TOML. */
export const DEPOSIT_LEVELS = 8;

/** The first protocol version whose State.beams is a list of per-row
 * deltas beside State.beams_gone (R2442). A replay recorded at an older
 * protocol carries the whole beam list on every frame. */
export const BEAMS_DELTA_SINCE = 36;

/** How many fleet control groups one empire holds: the digits 1 to N
 * (ui-map-interface.md §9, arcade.md §7.2, R539). Must match
 * hub.ControlGroupSlots — generated from the same TOML. */
export const CONTROL_GROUP_SLOTS = 6;

// The WebSocket close codes, in the 4000-4999 private range
// (data/contracts/protocol.toml, R359). A browser cannot read an HTTP
// status from a failed upgrade, so a refusal the client must act on is
// sent after it (R69). The servers declare the same numbers from the
// same table.
/**
 * The client's ?proto= does not match the server's ProtocolVersion. The
 * client recognises it and stops reconnecting for good: a stale build is
 * fixed by a reload, not by a retry (R69).
 */
export const CLOSE_PROTO_MISMATCH = 4001;
/**
 * The join ticket did not verify — malformed, signed by a key this
 * instance does not accept, expired, for another world, or absent on an
 * instance that runs auth "ticket" (deployment.md §3 step 4). The client
 * answers it with a fresh ticket and one reconnect, so it must never be
 * sent for a fault a new ticket cannot mend.
 */
export const CLOSE_TICKET_REJECTED = 4003;
/**
 * The season rolled and this world green-fielded its human seats, so the
 * seat under this socket is no longer this player's (deployment.md §5,
 * R766). The client asks the meta server for a placement — world 0, so
 * the assignment batch's reservation decides where it plays — and dials
 * the answer. The number is 4002 because it was free: 4001 is the
 * protocol mismatch (R69) and 4003 the refused ticket (R144), and the
 * three codes of one socket read best as one run.
 */
export const CLOSE_SEASON_ROLLED = 4002;
/**
 * A maintenance window opened (the release manager, deployment.md §6.1).
 * The world closes every socket with it the moment the gate goes up. The
 * client shows the maintenance cover in place of the map and polls
 * `/maintenance.json`; it reloads the page by itself when that document
 * is gone. It does not reconnect, because the game instances stop behind
 * the gate and a retry finds nothing that answers.
 */
export const CLOSE_MAINTENANCE = 4004;
/**
 * A `?spectate=<code>` dial the coordinator cannot serve (arcade.md
 * §15.6): no match and no replay holds the code, the operator ticket did
 * not verify, or the match already carries as many public spectators as
 * `spectators_max` allows. The client shows the refusal and does not
 * reconnect: a second dial answers the same way, and a finished match is
 * reached by its file route instead.
 */
export const CLOSE_SPECTATE_REFUSED = 4005;
/**
 * A `?watch=1` dial the world cannot serve (deployment.md §3.1, R2497,
 * R2498): the dial names no seat it may resume, the seat holds no empire,
 * the world is an arcade match, or the watch sockets of the seat or of the
 * world are at their cap. A watch founds no empire and takes no free seat.
 * The sea view shows the refusal and does not reconnect: a second dial
 * answers the same way until the player opens the map or closes a tab.
 */
export const CLOSE_WATCH_REFUSED = 4006;
/**
 * No session. The client signs in and dials again.
 */
export const CLOSE_CHAT_UNAUTHORIZED = 4100;
/**
 * The account is in no clan. There is no channel, and the client must not
 * reconnect until the account joins one.
 */
export const CLOSE_CHAT_NO_CLAN = 4101;
/**
 * Too many sockets from this address.
 */
export const CLOSE_CHAT_RATE_LIMITED = 4102;
/**
 * The server cannot serve chat at all.
 */
export const CLOSE_CHAT_UNAVAILABLE = 4103;

// The core world's default grid and the hex geometry (world.toml
// [world.topology]; the hex-vectors fixture pins both sides).
export const DEFAULT_COLS = 133;
export const DEFAULT_ROWS = 153;
export const COL_PITCH = 40;
export const HEX_SIZE = COL_PITCH / Math.sqrt(3);
export const MPX_PER_PX = 1000;
export const BLOCK_TRENCH = 1;
export const BLOCK_RIDGE = 2;

// The light of the world clock (world.toml [world.day], R2144): day
// runs from DAWN_HOUR to DUSK_HOUR. The day cue sounds on each crossing,
// and the hub pays the club share of the casino on the same crossing.
export const DAWN_HOUR = 6;
export const DUSK_HOUR = 18;

/** The NPC installations' listening range in px (world.toml, R112). */
export const FACTION_BASE_SONAR = 400;

/** A faction anchor's distance from the map centre, as a fraction of
 * the world width (world.toml). The chart furniture's ribbons hang on
 * the same anchors the server places a faction's water round (R548). */
export const ANCHOR_RADIUS_FRAC = 0.38;

// Faction and resource identity (identity.toml). `charge` is the
// heraldic device the chart's historical shield bears (R548); `short`
// is the chart's two-letter abbreviation for a commodity.
export const FACTIONS = [
  { id: 1, name: 'Abyssal Compact', color: 0x35e0d2, charge: 'waves' },
  { id: 2, name: 'Rust Fleet', color: 0xffb454, charge: 'gears' },
  { id: 3, name: 'Mariana Sisters', color: 0xff5c8a, charge: 'bezants' },
  { id: 4, name: 'Kelp Syndicate', color: 0x7ee787, charge: 'frond' },
  { id: 5, name: 'Void Choir', color: 0xb18cff, charge: 'chevronels' },
];

// The fleet designator letter and the first hull pool name of each
// faction (names.toml, R44, R46), in faction id order. The naming
// diagram of WARDROOM · FLEET NAMES reads them (R2186).
export const HULL_POOLS = [
  { faction: 1, letter: 'A', first: 'Nautilus' },
  { faction: 2, letter: 'R', first: 'Ingot' },
  { faction: 3, letter: 'M', first: 'Mariana' },
  { faction: 4, letter: 'K', first: 'Frond' },
  { faction: 5, letter: 'V', first: 'Threnody' },
] as const;

// The commodity names, one table per language (i18n.md §1.1, R858).
// The rest of a RESOURCES row is the same in every language.
type ResourceNames = Partial<Record<string, string>>;
const resourceNames_de: ResourceNames = {
  alloy: 'Legierung',
  biomass: 'Biomasse',
  volatiles: 'Volatile',
  isotopes: 'Isotope',
};
const resourceNames: ResourceNames = LOCALE === 'de' ? resourceNames_de : {};

export const RESOURCES = [
  { id: 1, key: 'alloy', name: resourceNames.alloy ?? 'Alloy', short: 'Al', glyph: '⬡', color: 0xb9c7d2 },
  { id: 2, key: 'biomass', name: resourceNames.biomass ?? 'Biomass', short: 'Bi', glyph: '✦', color: 0x7ee787 },
  { id: 3, key: 'volatiles', name: resourceNames.volatiles ?? 'Volatiles', short: 'Vo', glyph: '◈', color: 0xb18cff },
  { id: 4, key: 'isotopes', name: resourceNames.isotopes ?? 'Isotopes', short: 'Is', glyph: '☢', color: 0xd8e84a },
  { id: 5, key: 'credits', name: resourceNames.credits ?? 'Credits', short: 'Cr', glyph: '¤', color: 0xd8c66a },
] as const;

// The cross-service meta layer (meta.toml): the squad plan and the
// chat cap, the same generated figures the servers hold.
export const SQUAD_COUNT = 1;
/** How many members one squad holds. */
export const SQUAD_SIZE = 10;
export const CHAT_MAX_LEN = 240;

// The clan social rule (meta.toml [meta.social]): the roster cap, the
// player name rule and the clan name and tag rule. The same generated
// figures the meta server holds (R351).
/** How many members one clan holds. */
export const MAX_CLAN_MEMBERS = 10;
/** The player name rule (R226), counted in runes by the meta server. */
export const NAME_MIN_LEN = 3;
export const NAME_MAX_LEN = 24;
/** The clan name rule (R351). */
export const CLAN_NAME_MIN_LEN = 3;
export const CLAN_NAME_MAX_LEN = 32;
/** The clan tag rule (R351). A tag is drawn in brackets beside a
 * member's name, so it stays short enough to sit there. */
export const CLAN_TAG_MIN_LEN = 2;
export const CLAN_TAG_MAX_LEN = 5;

// The clan chat channel (meta.toml [meta.social], R222).
/** The meta server's body cap, in characters. The client refuses a
 * longer body before it sends it, so a player learns the limit from the
 * box and not from an error frame. */
export const CHAT_BODY_LIMIT = 500;
/** How many entries the client keeps, in the clan transcript and in the
 * world chat box alike. Four times the clan ring that a channel keeps for
 * replay: enough to scroll back
 * through a session, and bounded so a day-long tab does not grow. No
 * server reads it. */
export const CHAT_ENTRY_LIMIT = 200;
/** What a clan application's word may hold, in runes (R228). The meta
 * server refuses a longer note, so the box is capped at the same figure
 * and a player learns the limit from the box. */
export const CLAN_NOTE_RUNES = 280;

// The hours match of FIND A CLAN (meta.toml [meta.clan_hours], R1908).
/** How many members of a clan must state the reader's hours before the
 * clan matches. The FIND A CLAN card states the figure; the meta server
 * applies it. */
export const CLAN_HOURS_MIN_MEMBERS = 2;

// The report (meta.toml [meta.moderation], deployment.md §7.6). The meta
// server refuses a reason off the list and a longer note, so the report
// form offers the same classes and caps its box at the same figure.
export const REPORT_REASONS = ['spam', 'harassment', 'hate', 'sexual', 'violence', 'impersonation', 'illegal', 'other'] as const;
export const REPORT_NOTE_RUNES = 500;

// The error report (meta.toml [meta.faults], fault-reports.md §2, R1582).
// The client cuts a report to these figures before it sends it, and the
// meta server refuses a longer one. The bucket holds for one page load.
export const FAULT_MESSAGE_MAX_RUNES = 300;
export const FAULT_STACK_MAX_RUNES = 4000;
export const FAULT_TAG_MAX_LEN = 64;
export const FAULT_CLIENT_BURST = 5;
export const FAULT_CLIENT_REFILL_SEC = 60;

// The player report (meta.toml [meta.player_reports], fault-reports.md
// §4, R1583). The meta server refuses a topic off the list and a larger
// part, so the REPORT sheet offers the same topics and fits the same caps.
export const PLAYER_REPORT_TOPICS = ['bug', 'display', 'controls', 'connection', 'balance', 'other'] as const;
export const PLAYER_REPORT_TEXT_MAX_RUNES = 2000;
export const PLAYER_REPORT_LOG_TAIL_MAX_RUNES = 16000;
export const PLAYER_REPORT_SCREENSHOT_MAX_BYTES = 524288;
export const PLAYER_REPORT_FLEET_IDS_MAX = 32;

// The Captain Designer (meta.toml [meta.captain_designer], monetization.md
// §3). The meta server refuses a longer text, so the hub caps its fields
// at the same figures, in runes.
export const CAPTAIN_NAME_MIN_RUNES = 2;
export const CAPTAIN_NAME_MAX_RUNES = 24;
export const CAPTAIN_BACKSTORY_MAX_RUNES = 600;
export const CAPTAIN_VOICE_MAX_LINES = 8;
export const CAPTAIN_VOICE_LINE_MAX_RUNES = 120;

// The Fleet Designer (meta.toml [meta.fleet_designer], monetization.md
// §3.2). The meta server refuses a longer pool, so the hub caps its list
// at the same figures. A name bound counts runes of the name part.
export const FLEET_NAME_MIN_RUNES = 2;
export const FLEET_NAME_MAX_RUNES = 20;
export const FLEET_POOL_MAX_NAMES = 48;

// The free text of a seamount name (meta.toml [meta.seamount_names],
// v2-the-deep.md §4.3, R2610). The hub refuses a shorter or a longer
// name, so the naming sheet caps its field at the same figures, in runes.
export const SEAMOUNT_NAME_MIN_RUNES = 2;
export const SEAMOUNT_NAME_MAX_RUNES = 24;

// The free text of a cache note (meta.toml [meta.cache_notes],
// v2-the-deep.md §9.4, R2631). The hub refuses a shorter or a longer
// note, so the deposit sheet caps its field at the same figures, in runes.
export const CACHE_NOTE_MIN_RUNES = 2;
export const CACHE_NOTE_MAX_RUNES = 80;

// The advisor line (meta.toml [meta.advisor], player-reach.md §4.1,
// R1305): on by default for an account that has played at most this many
// Core seasons, the running season included.
export const ADVISOR_DEFAULT_ON_SEASONS = 2;

// The leave of absence cover (meta.toml [meta.leave], autopilot.md
// §14.1, R3283): the spans, in days, the ON LEAVE row of SETTINGS offers.
// The meta server refuses any other span.
export const LEAVE_DAYS: readonly number[] = [3, 7, 14];

// Absence cover (autopilot.toml absence_cover_after_hours, autopilot.md
// §14.1, R3283): the WALL hours with no client after which standing orders
// keep a commanded Core empire. The hub page opens no world socket, so the
// SETTINGS sentence reads the figure from here.
export const ABSENCE_COVER_AFTER_HOURS = 24;

// The custom lobby of a game host (meta.toml [meta.custom.lobby],
// deployment.md §6.7, R3088). The lobby panel reads each default and
// range here, and the meta server refuses a setting outside its range.
// The most commanders is the picked seats. `maxWallHours` is the wall cap
// of a custom run ([meta.custom] max_wall_hours): the wall length is
// seasonDays x 86400 / timescale seconds.
export const CUSTOM_LOBBY = {
  seats: [50],
  minPopulation: 2,
  defaultPopulation: 50,
  timescale: { min: 1, max: 30, default: 14 },
  tacticalScale: { min: 30, max: 120, default: 60 },
  combatPace: { min: 10, max: 60, default: 30 },
  seasonDays: { min: 2, max: 60, default: 28 },
  truceSec: { min: 1800, max: 172800, default: 43200 },
  stage1Frac: { min: 0.1, max: 0.5, default: 0.25 },
  stage2Frac: { min: 0.05, max: 0.25, default: 0.107 },
  minFactions: 2,
  maxFactions: 5,
  squads: 1,
  openTimeoutSec: 86400,
  draftTTLSec: 86400,
  pollSec: 2,
  budgetFallbackDays: 7,
  minHumans: 2,
  maxWallHours: 72,
} as const;

// The arcade ladder (meta.toml [meta.ladder], arcade.md §18.5, R1616):
// the ladder number at which each ladder tier from the second up starts,
// and the ladder number of an account with no rated match. The client
// reads the tier of a ladder number by the first list and starts the bar
// of the first tier at the second figure.
export const LADDER_TIER_FROM = [1100, 1300, 1500, 1700, 1900] as const;
export const LADDER_START = 800;

// The sea trials (trials.toml, player-reach.md §8, R1572): one row a
// step, in the bit order of `Empire.seaTrials`. SHOW opens `tab` on
// `sub` and gives no order.
export const SEA_TRIALS = [
  { code: "queue_building", tab: "BUILD", sub: "BASE" },
  { code: "start_research", tab: "BUILD", sub: "TECH" },
  { code: "launch_fleet", tab: "FLEET", sub: "MISSIONS" },
  { code: "build_dredger", tab: "BUILD", sub: "SHIPYARD" },
  { code: "send_gather", tab: "FLEET", sub: "MISSIONS" },
  { code: "build_beacon", tab: "BUILD", sub: "SHIPYARD" },
  { code: "take_cell", tab: "WAR", sub: "SOVEREIGNTY" },
  { code: "recruit_captain", tab: "CAPTAIN", sub: "RECRUITMENT" },
] as const;

// The quick lines (quickchat.toml, ui-map-interface.md §8 "Quick chat",
// R1607): one row a line, in line order, so row i is line i+1 on the
// wire. `carries` is what the line names, in the order the sender's
// client tries them (R1608).
export const QUICK_LINES = [
  { code: "help", carries: ['marker'] },
  { code: "attack", carries: ['marker', 'fleet'] },
  { code: "defend", carries: ['hold'] },
  { code: "enemy", carries: ['contact', 'marker'] },
  { code: "siege", carries: ['object'] },
  { code: "coming", carries: ['fleet'] },
  { code: "withdraw", carries: ['fleet'] },
  { code: "wait", carries: [] },
  { code: "regroup", carries: ['marker'] },
  { code: "yes", carries: [] },
  { code: "no", carries: [] },
  { code: "well_done", carries: [] },
] as const;

// The account signal of the multi-account detection (meta.toml
// [meta.fairplay], deployment.md §7.7, R1310): the life of the device id
// cookie, and how long the collector waits for the WebGPU adapter.
export const FAIRPLAY_DEVICE_COOKIE_DAYS = 365;
export const FAIRPLAY_COLLECT_TIMEOUT_MS = 1000;

// The support address (meta.toml [meta.contact], R604). It travels in
// TWO halves and is joined nowhere but the browser, at the moment a
// person presses for it, so this bundle holds no address to harvest.
/** The name before the "@". */
export const SUPPORT_MAIL_USER = 'support';
/** The domain after the "@". */
export const SUPPORT_MAIL_DOMAIN = 'aquaphobia.io';

// The community server's invite (meta.toml [meta.contact], R612). The
// static contact page reads the same value. Empty means there is none.
export const DISCORD_URL = 'https://discord.gg/kFXDBA4Z4';

// The figures the hub's FAQ states (R355). The hub opens no world
// socket, so these ride the compile-time lane and not the hello
// catalog: they are the DESIGNED figures, not one world's patched pack.
/** How long the build queue may hold, in world hours, and what the
 * extended-queue key raises it to. */
export const MAX_QUEUE_HOURS = 12;
export const MAX_QUEUE_HOURS_EXTENDED = 48;
/** How many days a season runs (hub.toml). */
export const SEASON_DAYS = 28;
/** The captain career (numbers.md §12): how many attributes there are,
 * the ceiling, what one point buys, the age arc in years and the
 * wardroom. */
export const CAPTAIN_ATTRS = 6;
export const ATTR_TRAIN_CAP = 10;
export const ATTR_POINT_GAIN = 3;
export const YEARS_PER_SEASON = 5;
export const RECRUIT_AGE_MIN = 20;
export const RECRUIT_AGE_MAX = 40;
export const PEAK_AT_AGE = 40;
export const DECLINE_AT_AGE = 60;
export const CAPTAIN_SLOTS_BASE = 3;
export const CAPTAIN_SLOTS_MAX = 6;
/** The Signal Station level that shares a squadmate's scan (R83). */
export const SHARED_INTEL_LEVEL = 3;
/** One account's arcade runs a day with no plan. An operator may raise
 * it, so the PLAY tile is the true figure for a deployment. */
export const ARCADE_RUNS_FREE = 5;
/** A guest account's arcade runs a day with no plan. */
export const ARCADE_RUNS_GUEST = 3;

/** The bank one arcade seat opens with when the map names no other
 * figure (arcade.toml, R282) — the designer's blank map states it once. */
export const DEFAULT_STARTING_CREDITS = 100;

/** The share of the season that is left when seaquake stage 1 opens
 * (hub.toml [hub.quake], numbers.md §15). The hub's guest card reads the
 * designed figure, because the hub opens no world socket (R1565). */
export const QUAKE_STAGE1_FRAC = 0.25;

/** The arcade daemon's own default port (ops.toml
 * [ops.arcadecoord] addr). It belongs to no lane, and the client dials
 * it when the page names no host: one figure, both ends. */
export const ARCADE_PORT = 8096;

// The map designer's authoring surface (arcade.toml [arcade.designer],
// arcade.md §4.3-§4.4). Client-only: no server package reads it.
export const PRESETS = [
  { key: 'xxs', label: 'XXS', cols: 12, rows: 10, use: '1v1 knife fight' },
  { key: 'xs', label: 'XS', cols: 16, rows: 12, use: '1v1 with room to flank' },
  { key: 's', label: 'S', cols: 20, rows: 16, use: '2–3 players' },
  { key: 'm', label: 'M', cols: 24, rows: 18, use: '3–6 players, two teams' },
  { key: 'l', label: 'L', cols: 28, rows: 22, use: '4–8 players' },
  { key: 'xl', label: 'XL', cols: 36, rows: 28, use: '6–10 players, three teams' },
  { key: 'xxl', label: 'XXL', cols: 48, rows: 36, use: 'full 15-player layouts' },
] as const;
export const DEFAULT_PRESET_KEY = 'm';
/** The pacing a map file takes when it omits the field (R360). The
 * server's arcade.LoadFile fills an absent value from the same three
 * figures, so a map states only what it does differently. */
export const DESIGNER_DEFAULT_TIMESCALE = 1;
export const DESIGNER_DEFAULT_TACTICALSCALE = 60;
export const DESIGNER_DEFAULT_TIMEOUT_SEC = 1200;
/** The combat clock a match runs at when the map file names no other
 * figure (R408). The arcade keeps its own pace: core divides every damage
 * rate by sim.combat_pace_divisor, and the arcade default is a separate
 * figure per mode (R1111). */
export const DESIGNER_DEFAULT_COMBAT_PACE_DIVISOR = 30;
/** The natural shield regen of a match in WALL seconds when the map file
 * names no other figure (R2010): the calm gate, and the time of a refill
 * from an empty shield to the maximum. */
export const DESIGNER_DEFAULT_REGEN_CALM_SEC = 60;
export const DESIGNER_DEFAULT_REGEN_REFILL_SEC = 180;
export const DESIGNER_DEFAULT_CREDITS_PER_SEC = 2.5;
/** The period in wall seconds a new auto spawn opens with (arcade.md
 * §10.3, R2004). */
export const DESIGNER_DEFAULT_AUTO_SHIP_EVERY_SEC = 90;
export const DESIGNER_DEFAULT_AUTO_TORPEDO_EVERY_SEC = 120;
/** The economy a blank map opens with (R1995): the stock of a field that
 * states none, and the credits one mined unit pays. */
export const DESIGNER_DEFAULT_RESOURCE_FIELD_UNITS = 1000;
export const DESIGNER_DEFAULT_MINED_CREDIT_RATE = 0.5;
/** The parameters a win condition opens with when the author picks its
 * type (arcade.md §4.5, R1995). Every hold is wall seconds (R397). */
export const DESIGNER_WIN_DEFAULTS = {
  sovSharePercent: 40,
  sovShareHoldSec: 30,
  sovCellHoldSec: 60,
  mineUnits: 500,
  killClass: 'Dredger',
  killClassCount: 4,
  killsCount: 8,
  scanClass: 'Bombard',
  scanClassCount: 3,
  holdStructuresCount: 1,
  holdStructuresHoldSec: 30,
} as const;
/** The symmetry kind a new map opens with, by its seat count (arcade.md
 * §10.3, R2025). A seat count the table does not name opens with none. */
export const DESIGNER_SYMMETRY_BY_SEATS: Readonly<Record<number, string>> = { 2: 'rot180', 3: 'rot120', 4: 'rot180' };
export const CATALOG_DEFAULT_ROLL = 1;

/** The community bounds (arcade-designer.toml [arcade.designer.bounds],
 * community-maps.md §3.3 rule 6, R2009): the ranges a community map of an
 * author who is not an admin may state. The meta server reads the same
 * table. */
export const COMMUNITY_BOUNDS = {
  timescale: { min: 0.25, max: 4 },
  tacticalscale: { min: 15, max: 240 },
  timeout_sec: { min: 120, max: 3600 },
  combat_pace_divisor: { min: 5, max: 180 },
  regen_calm_sec: { min: 20, max: 3600 },
  regen_refill_sec: { min: 30, max: 3600 },
  credits_per_sec: { min: 0, max: 20 },
  starting_credits: { min: 0, max: 10000 },
  mined_credit_rate: { min: 0, max: 2 },
  resource_field_units: { min: 0, max: 10000 },
  match_credits: { min: 0, max: 60000 },
  ship_price: { min: 20, max: 1e+06 },
  scan_cooldown_sec: { min: 0, max: 600 },
  auto_ship_every_sec: { min: 30, max: 3600 },
  auto_torpedo_every_sec: { min: 5, max: 3600 },
  capture_sec: { min: 3, max: 120 },
  guard_reach_px: { min: 30, max: 200 },
  guard_reload_sec: { min: 2, max: 60 },
  current_scale: { min: 0.25, max: 2 },
  den_wave_sec: { min: 60, max: 600 },
  lair_respawn_sec: { min: 0, max: 900 },
  lair_bounty: { min: 50, max: 1000 },
} as const;

// The credit price the map designer pre-fills for a hull class and for
// a round (arcade.toml [arcade.economy], arcade.md §4.6, R399). Each
// figure is DERIVED at generation time from the material cost of the
// hull or the round, rounded to DERIVED_PRICE_STEP. The client holds
// the answer, never the cost table it came from: a static designer has
// no hello catalog to read one from. The author owns the final number.
export const DERIVED_PRICE_STEP = 5;
export const DERIVED_SHIP_PRICE: Record<string, number> = {
  'Corsair': 125,
  'Cutlass': 115,
  'Lancer': 185,
  'Falconet': 110,
  'Basilisk': 275,
  'Bombard': 550,
  'Commodore': 390,
  'Vedette': 120,
  'Dredger': 185,
  'Sentinel': 245,
  'Beacon': 525,
  'Oracle': 580,
  'Tender': 965,
  'Sapper': 555,
};
export const DERIVED_TORPEDO_PRICE: Record<string, number> = {
  'Harpoon': 10,
  'Remora': 15,
  'Torpor': 15,
  'Culverin': 85,
  'Caltrop': 5,
  'Bulwark': 55,
  'Countermine': 20,
  'Stonefish': 20,
  'Wrasse': 25,
  'Sepia': 25,
  'Sailfish': 25,
};

// The derived price and the cargo of each sea object a map may sell, by
// the name a map file spells, and the cargo of the Sapper (arcade.md §4.3,
// §4.6, R3220).
export const DERIVED_SEA_OBJECT_PRICE: Record<string, number> = {
  'Barb': 10,
  'Bola': 15,
  'Siren': 20,
  'Petard': 40,
  'Seine': 115,
  'Cheval': 100,
  'Hark': 130,
  'Feint': 45,
  'Palisade': 215,
};
export const SEA_OBJECT_CARGO: Record<string, number> = {
  'Barb': 10,
  'Bola': 10,
  'Siren': 15,
  'Petard': 25,
  'Seine': 30,
  'Cheval': 25,
  'Hark': 20,
  'Feint': 10,
  'Palisade': 60,
};
export const SEA_OBJECT_LINES: readonly string[] = ['Seine', 'Palisade'];
export const SEA_MINE_KINDS: readonly string[] = ['Barb', 'Bola', 'Siren', 'Petard'];
export const SAPPER_CLASS = 'Sapper';
export const SAPPER_CARGO = 100;

// The cargo room of one hull round and the magazine of a tubed hull, by
// the name a map file spells (numbers.md §5, arcade.md §4.6, R3215). The
// validator checks a rounds mix against them (`map.ships.rounds_fits`),
// and the map designer sums a mix against the magazine.
export const ROUND_CARGO: Record<string, number> = {
  'Harpoon': 5,
  'Remora': 5,
  'Torpor': 5,
  'Countermine': 5,
  'Stonefish': 5,
  'Wrasse': 5,
  'Sepia': 5,
  'Sailfish': 5,
};
export const MAGAZINE_CARGO: Record<string, number> = {
  'Lancer': 60,
  'Falconet': 10,
  'Basilisk': 20,
};

// The hull rows and the round speeds of the pack (ships.toml and
// torpedoes.toml; numbers.md §3, §4, §5). `speedUh` is world px per map
// hour, and the ranges are world px. The reel page builds its hello
// catalog from these rows and moves its staged hulls and rounds at the
// speed times its map scale (R1535): it opens no socket, so no hello
// catalog of a server reaches it. A live server's `-balance` patch does
// not move them.
export const SHIP_ROWS: readonly {
  id: string; speedUh: number; range: number; minRange: number; anchorRange: number; role: string;
}[] = [
  { id: 'Corsair', speedUh: 800, range: 30, minRange: 0, anchorRange: 0, role: 'skirmish' },
  { id: 'Cutlass', speedUh: 500, range: 50, minRange: 0, anchorRange: 0, role: 'brawler' },
  { id: 'Lancer', speedUh: 450, range: 30, minRange: 0, anchorRange: 0, role: 'torpedo' },
  { id: 'Falconet', speedUh: 400, range: 75, minRange: 30, anchorRange: 0, role: 'artillery' },
  { id: 'Basilisk', speedUh: 300, range: 90, minRange: 40, anchorRange: 0, role: 'artillery' },
  { id: 'Bombard', speedUh: 250, range: 50, minRange: 0, anchorRange: 0, role: 'brawler' },
  { id: 'Commodore', speedUh: 400, range: 50, minRange: 0, anchorRange: 0, role: 'command' },
  { id: 'Vedette', speedUh: 700, range: 0, minRange: 0, anchorRange: 0, role: 'support' },
  { id: 'Dredger', speedUh: 350, range: 0, minRange: 0, anchorRange: 0, role: 'support' },
  { id: 'Sentinel', speedUh: 100, range: 55, minRange: 0, anchorRange: 70, role: 'tower' },
  { id: 'Beacon', speedUh: 150, range: 0, minRange: 0, anchorRange: 0, role: 'support' },
  { id: 'Oracle', speedUh: 250, range: 0, minRange: 0, anchorRange: 0, role: 'support' },
  { id: 'Tender', speedUh: 200, range: 0, minRange: 0, anchorRange: 0, role: 'support' },
];
export const TORPEDO_SPEED_UH: Record<string, number> = {
  'light': 3000,
  'seeker': 3000,
  'disruptor': 3000,
  'heavy': 2400,
  'interceptor': 3500,
  'bulwark': 2400,
  'pod': 3000,
  'countermine': 3000,
  'stonefish': 3000,
  'wrasse': 3000,
  'sepia': 3000,
  'sailfish': 3000,
};
// The splash in world px and the damage of each round (torpedoes.toml;
// numbers.md §5), for the detonation look (R2079) of a page that no hello
// catalog reaches. A live server's `-balance` patch does not move them.
export const TORPEDO_DETONATION: Record<string, { splash: number; damage: number }> = {
  'light': { splash: 20, damage: 120 },
  'seeker': { splash: 10, damage: 150 },
  'disruptor': { splash: 40, damage: 15 },
  'heavy': { splash: 20, damage: 300 },
  'interceptor': { splash: 5, damage: 45 },
  'bulwark': { splash: 15, damage: 200 },
  'pod': { splash: 12, damage: 90 },
  'countermine': { splash: 90, damage: 20 },
  'stonefish': { splash: 20, damage: 100 },
  'wrasse': { splash: 40, damage: 0 },
  'sepia': { splash: 40, damage: 0 },
  'sailfish': { splash: 40, damage: 0 },
};

// Arcade map-schema limits (arcade.toml; arcade.md §4.3 and §4.5).
export const MAX_PLAYERS = 15;
export const MAX_TEAMS = 5;
export const MAX_PER_TEAM = 3;
export const MIN_FFA_PLAYERS = 2;
export const MAX_WIN_TYPES = 1;
export const MAX_SONAR_PX = 4000;
export const MAX_PRICE_CREDIT = 1000000;
export const MAX_MARKERS = 64;
export const MAX_HEADING_DEG = 359;
export const NUM_FACTIONS = 5;
export const MIN_COLS = 8;
export const MIN_ROWS = 8;
export const MAX_COLS = 80;
export const MAX_ROWS = 96;
/** The most rounds of one kind a map may grant a seat at founding
 * (R398). The cap is flat: a round is a round. */
export const MAX_STARTING_TORPEDOES = 50;
/** The highest frozen map bit (arcade.md §4.1, R955). Every active map
 * file carries a `bit`, and `arcade_map_mask` travels as a signed int64,
 * so the folder holds 63 active maps at most. A bit belongs to the MAP:
 * a new drawing of a map keeps it, and it is never handed to another
 * map. */
export const MAX_MAP_BIT = 62;

// The module bounds a map file may name (arcade.md §4.6, R355). The
// invention pair is the compile-time copy of the blueprint draw range:
// a roll outside it only warns, because the map is legal but no
// invention could produce the module.
export const MIN_MODULE_TIER = 1;
export const MAX_MODULE_TIER = 3;
export const MIN_MODULE_ROLL = 0.5;
export const MAX_MODULE_ROLL = 1.5;
export const MIN_INVENTION_ROLL = 0.7;
export const MAX_INVENTION_ROLL = 1.5;

/** The length of a hosting code (arcade.md §16.1). A hosting code is
 * drawn from THEME_CODE_ALPHABET: it holds no vowel, so a code cannot
 * spell a word. It is read out over a voice channel, so it is short. A
 * person types it in any case. */
export const HOST_CODE_LEN = 3;

/** The first symbol of a three-symbol code (R3087). A code that starts
 * with one of HOST_CODE_HEADS is an arcade hosting code; a code that
 * starts with one of CUSTOM_CODE_HEADS is a custom lobby code of the meta
 * server. Together the two sets are THEME_CODE_ALPHABET. */
export const HOST_CODE_HEADS = 'BCDFGHJKMNPQRSTVWXYZ';
export const CUSTOM_CODE_HEADS = '23456789';

/** The four ceilings of a drill (arcade.md §17, R1564): the drill steps of
 * one map, the hulls one drill step spawns, the acts one counted drill step
 * asks for, and the radius of a map spot in world px. */
export const MAX_DRILL_STEPS = 16;
export const MAX_DRILL_SPAWNS = 8;
export const MAX_DRILL_COUNT = 20;
export const MAX_DRILL_SPOT_PX = 600;

/** The longest step wait in wall seconds: the time a drill step stands
 * after its done check is met (arcade.md §17.2, R1713). */
export const MAX_DRILL_WAIT_SEC = 10;

/** The most minutes a drill map may state: the time a new player needs
 * for the whole drill (arcade.md §17.2, R1946). */
export const MAX_DRILL_MINUTES = 60;

/** The capture time of an arcade structure in WALL seconds (arcade.md
 * §19.2, R2021): the bounds of a map's capture_sec, and the figure a 0 or
 * an absent field takes. */
export const MIN_CAPTURE_SEC = 1;
export const MAX_CAPTURE_SEC = 600;
export const DESIGNER_DEFAULT_CAPTURE_SEC = 10;

/** The reach in world px and the reload in WALL seconds of a Reef
 * Breakwater (arcade.md §19.9, R2085): the bounds of a structure's reach_px
 * and reload_sec, and the figure a 0 or an absent field takes. */
export const MIN_GUARD_REACH_PX = 20;
export const MAX_GUARD_REACH_PX = 400;
export const DESIGNER_DEFAULT_GUARD_REACH_PX = 70;
export const MIN_GUARD_RELOAD_SEC = 1;
export const MAX_GUARD_RELOAD_SEC = 600;
export const DESIGNER_DEFAULT_GUARD_RELOAD_SEC = 4;

/** The current scale of a map that sets `currents` (arcade.md §4.3,
 * R3214): the bounds of a map's current_scale, and the figure a 0 or an
 * absent field takes. */
export const MIN_CURRENT_SCALE = 0.1;
export const MAX_CURRENT_SCALE = 3;
export const DESIGNER_DEFAULT_CURRENT_SCALE = 0.5;

/** The NPC side of a match (arcade.md §20, R3216): the bounds of the wave
 * timer of a den and of the respawn of a lair in WALL seconds, the most
 * bounty of a lair in credits, the figures a 0 takes (a 0 respawn is one
 * beast a match), the respawn a new lair opens with, and the count of the
 * designs of a den and of a lair. */
export const MIN_DEN_WAVE_SEC = 30;
export const MAX_DEN_WAVE_SEC = 1800;
export const DESIGNER_DEFAULT_DEN_WAVE_SEC = 120;
export const MIN_LAIR_RESPAWN_SEC = 30;
export const MAX_LAIR_RESPAWN_SEC = 1800;
export const DESIGNER_DEFAULT_LAIR_RESPAWN_SEC = 0;
export const MAX_LAIR_BOUNTY = 5000;
export const DESIGNER_DEFAULT_LAIR_BOUNTY = 300;
export const DEN_DESIGNS = 2;
export const LAIR_DESIGNS = 4;

/** The sea objects of a match (arcade.md §4.3, R3220): the ceiling of
 * a row cap and of a starting grant, the cap a 0 takes, and the community
 * bounds of a row price, a row cap and the laid list. */
export const MAX_SEA_OBJECT_CAP = 60;
export const MAX_STARTING_SEA_OBJECTS = 30;
export const DESIGNER_DEFAULT_SEA_OBJECT_CAP = 6;
export const SEA_OBJECT_BOUNDS = {
  price: { min: 10, max: 1e+06 },
  cap: { min: 1, max: 30 },
  laid_per_map: { min: 0, max: 40 },
} as const;

/** The painted fairways of a map file (arcade.md §4.3, R3225): the most
 * chains a map lists, and the fewest cells of one chain. */
export const MAX_FAIRWAY_CHAINS = 8;
export const MIN_FAIRWAY_CELLS = 4;

// The casino's wire vocabulary (data/contracts/casino.toml,
// casino.md §3). The index is what the wire carries: a stake tier on
// CasinoQueueJoin, a bet kind on CasinoAct, and a symbol on every reel
// of CasinoResult. The casino's FIGURES arrive in the hello catalog
// (R355), never from this file.
export const CASINO_TIERS = ['low', 'mid', 'high'] as const;
/** Every classical roulette bet kind, in wire order. `covers` is how
 * many of the 38 pockets the kind covers and `inside` marks a bet
 * placed on the number grid; the client validates adjacency for the
 * highlight alone, and the server rules. */
export const CASINO_BET_KINDS = [
  { code: 'straight', covers: 1, inside: true },
  { code: 'split', covers: 2, inside: true },
  { code: 'street', covers: 3, inside: true },
  { code: 'corner', covers: 4, inside: true },
  { code: 'line', covers: 6, inside: true },
  { code: 'dozen', covers: 12, inside: false },
  { code: 'column', covers: 12, inside: false },
  { code: 'red', covers: 18, inside: false },
  { code: 'black', covers: 18, inside: false },
  { code: 'odd', covers: 18, inside: false },
  { code: 'even', covers: 18, inside: false },
  { code: 'low', covers: 18, inside: false },
  { code: 'high', covers: 18, inside: false },
] as const;
/** The slot symbols, in wire order. `blank` is the empty stop. */
export const CASINO_SYMBOLS = ['kraken', 'trident', 'pearl', 'shell', 'anchor', 'blank'] as const;
/** How many pockets the wheel holds. Two zeros, so the house edge
 * is 2/38 (R2752). */
export const CASINO_POCKETS = 38;

/** The record schema's own version (arcade.md §15.1, R920). Format 1 held
 * one full frame stream per channel; format 2 holds one omniscient stream on
 * channel 0 and one visibility mask per seat. The viewer must refuse a stream
 * whose format it does not know. */
export const REPLAY_FORMAT = 2;

/** THE CONTACT STRIP (arcade.md §15.1, R922). A seat channel carries no
 * frame, so the viewer builds an enemy contact out of the omniscient fleet
 * record of channel 0: it keeps these fields, it forces the fields of
 * REPLAY_CONTACT_SET, and it clears every other field. Seven of these stand
 * behind a disclosure gate on the server, which a mask of fleet ids cannot
 * state, so a contact built from this list alone stands at its widest grade.
 * TestContactStripFollowsTheTable in server/internal/hub holds the table
 * against the server's own enemy branch. */
export const REPLAY_CONTACT_KEEP = [
  'id',
  'faction',
  'x_mpx',
  'y_mpx',
  'cls',
  'hull_pct',
  'shield_pct',
  'weap_type',
  'shield_type',
  'anchor',
  'anchor_left_sec',
  'effect',
  'livery',
  'owner_name',
  'scan_left_sec',
  'heading_mrad',
  'signature_charge',
  'signature_active',
  'signature_left_sec',
  'signature_order',
] as const;

/** The fields the server FORCES on a contact, whatever the omniscient
 * record holds (R922). */
export const REPLAY_CONTACT_SET: Readonly<Record<string, number>> = {
  sonar_px: 0,
};

/** The kept fields a disclosure grade of the mask entry opens (R922): the
 * value names the grade, `cls_open`, `systems_open` or `condition_open`.
 * A gated field the mask does not open is cleared. */
export const REPLAY_CONTACT_GATED: Readonly<Record<string, 'cls_open' | 'systems_open' | 'condition_open'>> = {
  cls: 'cls_open',
  hull_pct: 'condition_open',
  owner_name: 'systems_open',
  shield_pct: 'condition_open',
  shield_type: 'systems_open',
  weap_type: 'systems_open',
};

/** The kept fields whose value the mask entry supplies, because the server
 * substitutes a value the omniscient record does not hold (R922). */
export const REPLAY_CONTACT_FROM_MASK = [
  'effect',
  'scan_left_sec',
] as const;

/** THE TEAMMATE STRIP (arcade.md §15.1, R923). The omniscient record of
 * channel 0 holds every hull at OWN grade. A seat, however, reads these
 * fields on the hulls it COMMANDS alone: where a hull is going, what it
 * carries, how ready its crew is, which line it will form. So a seat view
 * clears them on a hull of the seat's own faction that another commander
 * owns, which `owner_player` names. TestTeamStripFollowsTheTable in
 * server/internal/hub holds the list against pbFleet's own branch. */
export const REPLAY_TEAM_CLEAR = [
  'own',
  'deep',
  'deep_run',
  'battle_plan',
  'heard',
  'scan_ready_sec',
  'sonar_share_left_sec',
  'readiness_pct',
  'readiness_floor_pct',
  'noise_milli',
  'regen_in_sec',
  'reload_sec',
  'reload_full_sec',
  'hold',
  'cargo_cap',
  'shaft',
  'escorting',
  'focus',
  'chase',
  'torpedo_range',
  'rounds',
  'active_round',
  'tx_mpx',
  'ty_mpx',
  'px_mpx',
  'py_mpx',
  'pledge_site',
  'auto_field_off',
  'hold_fire',
  'guard',
  'quiet',
  'signature_floor_sec',
  'signature_target',
] as const;

/** Every rule id. The same ids, spelled the same way, live in
 * rules.go — both are written from arcade.toml (R262). */
export const RULES = {
  version: 'map.version',
  name: 'map.name',
  revision: 'map.revision',
  status: 'map.status',
  bitPresent: 'map.bit.present',
  bitRange: 'map.bit.range',
  bitStatus: 'map.bit.status',
  gridDims: 'map.grid.dims',
  terrainShape: 'map.terrain.shape',
  playerCount: 'map.players.count',
  playerSlotRange: 'map.players.slot_range',
  playerSlotUnique: 'map.players.slot_unique',
  playerTeamRange: 'map.players.team_range',
  playerTeamCount: 'map.players.team_count',
  playerTeamSize: 'map.players.team_size',
  timescale: 'map.settings.timescale',
  tacticalScale: 'map.settings.tacticalscale',
  timeout: 'map.settings.timeout_sec',
  combatPace: 'map.settings.combat_pace_divisor',
  shieldRegen: 'map.settings.shield_regen',
  credits: 'map.settings.credits',
  scanCooldown: 'map.settings.scan_cooldown_sec',
  resourceFieldUnits: 'map.settings.resource_field_units',
  captureSec: 'map.settings.capture_sec',
  currentScale: 'map.settings.current_scale',
  currentsSymmetry: 'map.settings.currents_symmetry',
  settingsFlat: 'map.settings.flat',
  shipsPresent: 'map.ships.present',
  shipClass: 'map.ships.class',
  shipClassRule: 'map.ships.class_rule',
  shipName: 'map.ships.name',
  shipPrice: 'map.ships.price',
  shipNotBuyable: 'map.ships.not_buyable',
  moduleFamily: 'map.ships.module_family',
  moduleTier: 'map.ships.module_tier',
  moduleRoll: 'map.ships.module_roll',
  moduleDuplicate: 'map.ships.module_duplicate',
  shipWeaponType: 'map.ships.weapon_type',
  shipShieldType: 'map.ships.shield_type',
  shipRoundsKind: 'map.ships.rounds_kind',
  shipRoundsFits: 'map.ships.rounds_fits',
  shipRoundsUnsold: 'map.ships.rounds_unsold',
  shipStowKind: 'map.ships.stow_kind',
  shipStowFits: 'map.ships.stow_fits',
  shipSapperIdle: 'map.ships.sapper_idle',
  torpKind: 'map.torpedoes.kind',
  torpUnique: 'map.torpedoes.unique',
  torpPrice: 'map.torpedoes.price',
  torpNoCarrier: 'map.torpedoes.no_carrier',
  torpNeedsLoad: 'map.torpedoes.needs_load',
  startTorpKind: 'map.starting_torpedoes.kind',
  startTorpUnique: 'map.starting_torpedoes.unique',
  startTorpCount: 'map.starting_torpedoes.count',
  startTorpNoCarrier: 'map.starting_torpedoes.no_carrier',
  seaObjKind: 'map.sea_objects.kind',
  seaObjUnique: 'map.sea_objects.unique',
  seaObjPrice: 'map.sea_objects.price',
  seaObjCap: 'map.sea_objects.cap',
  seaObjNeedsSapper: 'map.sea_objects.needs_sapper',
  startSeaObjKind: 'map.starting_sea_objects.kind',
  startSeaObjUnique: 'map.starting_sea_objects.unique',
  startSeaObjCount: 'map.starting_sea_objects.count',
  autoShipVariant: 'map.auto_spawn.ship_variant',
  autoShipPeriod: 'map.auto_spawn.ship_period',
  autoTorpKind: 'map.auto_spawn.torpedo_kind',
  autoTorpPeriod: 'map.auto_spawn.torpedo_period',
  objectCell: 'map.objects.cell_range',
  objectKind: 'map.objects.kind',
  objectOnRidge: 'map.objects.on_ridge',
  objectCellUnique: 'map.objects.cell_unique',
  basePerSlot: 'map.objects.base_per_slot',
  baseUnknownSlot: 'map.objects.base_unknown_slot',
  factionBaseTeam: 'map.objects.faction_base_team',
  factionBaseSonar: 'map.objects.faction_base_sonar',
  fieldResource: 'map.objects.field_resource',
  fieldUnits: 'map.objects.field_units',
  structureTeam: 'map.objects.structure_team',
  structureObjective: 'map.objects.structure_objective',
  structureFlag: 'map.objects.structure_flag',
  objectiveUnused: 'map.objects.objective_unused',
  guardReach: 'map.objects.guard_reach',
  guardReload: 'map.objects.guard_reload',
  denBrotherhood: 'map.objects.den_brotherhood',
  denDesign: 'map.objects.den_design',
  denWave: 'map.objects.den_wave',
  denApron: 'map.objects.den_apron',
  lairKind: 'map.objects.lair_kind',
  lairDesign: 'map.objects.lair_design',
  lairTrench: 'map.objects.lair_trench',
  lairRespawn: 'map.objects.lair_respawn',
  lairBounty: 'map.objects.lair_bounty',
  npcField: 'map.objects.npc_field',
  fleetCell: 'map.fleets.cell_range',
  fleetOnRidge: 'map.fleets.on_ridge',
  fleetVariant: 'map.fleets.variant',
  fleetSlot: 'map.fleets.slot',
  fleetClassCap: 'map.fleets.class_cap',
  sovCell: 'map.sov.cell_range',
  sovOnRidge: 'map.sov.on_ridge',
  sovTeam: 'map.sov.team',
  sovCellUnique: 'map.sov.cell_unique',
  sovRequiresEnabled: 'map.sov.requires_enabled',
  markerCell: 'map.markers.cell_range',
  markerShape: 'map.markers.shape',
  markerColor: 'map.markers.color',
  markerHeading: 'map.markers.heading',
  markerCount: 'map.markers.count',
  laidCell: 'map.laid.cell_range',
  laidKind: 'map.laid.kind',
  laidOnRidge: 'map.laid.on_ridge',
  laidOnObject: 'map.laid.on_object',
  laidTeam: 'map.laid.team',
  laidHeading: 'map.laid.heading',
  oneComponent: 'map.connectivity.single_component',
  winCount: 'map.win.count',
  winType: 'map.win.type',
  winParams: 'map.win.params',
  winMeans: 'map.win.means',
  winClass: 'map.win.class',
  winCell: 'map.win.cell',
  winSovEnabled: 'map.win.sov_requires_sov_enabled',
  winScanHull: 'map.win.scan_requires_vedette',
  winMineHull: 'map.win.mine_requires_gathering_hull',
  winMineField: 'map.win.mine_requires_resource_field',
  winObjectives: 'map.win.hold_requires_objectives',
  drillWin: 'map.drill.win',
  drillCount: 'map.drill.count',
  drillBit: 'map.drill.bit',
  drillStepID: 'map.drill.step_id',
  drillStepText: 'map.drill.step_text',
  drillHighlight: 'map.drill.highlight',
  drillDone: 'map.drill.done',
  drillBots: 'map.drill.bots',
  drillSpawn: 'map.drill.spawn',
  drillOrder: 'map.drill.order',
  drillWait: 'map.drill.wait',
  drillMinutes: 'map.drill.minutes',
  drillReset: 'map.drill.reset',
  drillSelect: 'map.drill.select',
  symmetryShape: 'map.symmetry.shape',
  symmetryTerrain: 'map.symmetry.terrain',
  symmetrySeats: 'map.symmetry.seats',
  symmetryContent: 'map.symmetry.content',
  symmetryWinCell: 'map.symmetry.win_cell',
  symmetryRequired: 'map.symmetry.required',
  fairwayCell: 'map.fairways.cell_range',
  fairwayOnRidge: 'map.fairways.on_ridge',
  fairwayChain: 'map.fairways.chain',
  fairwayCellUnique: 'map.fairways.cell_unique',
  fairwayCount: 'map.fairways.count',
} as const;

// The map-file vocabulary (arcade-schema.toml [arcade.vocabulary]).
// arcade/mapschema.ts re-exports every name below, and mapdef.go
// declares the Go twin of each one from the same table (R337).

/** Terrain characters. One character per cell, so a row is readable text. */
export const TERRAIN_WATER = '.';
export const TERRAIN_RIDGE = '^';
export const TERRAIN_TRENCH = 'v';
export type TerrainChar = '.' | '^' | 'v';

/** The status a map file carries (arcade.md §4.1). A lobby lane takes
 * an active file alone; a retired or a draft file still loads. */
export const STATUS_ACTIVE = 'active';
export const STATUS_RETIRED = 'retired';
export const STATUS_DRAFT = 'draft';
export const STATUS_COMMUNITY = 'community';
export const MAP_STATUSES = ['active', 'retired', 'draft', 'community'] as const;
export type MapStatus = 'active' | 'retired' | 'draft' | 'community';

/** Object kinds a tile can carry (arcade.md §4.7). */
export const OBJECT_PLAYER_BASE = 'player_base';
export const OBJECT_FACTION_BASE = 'faction_base';
export const OBJECT_RESOURCE_FIELD = 'resource_field';
export const OBJECT_TRENCH_THRONE = 'trench_throne';
export const OBJECT_CORAL_WARD = 'coral_ward';
export const OBJECT_DEEP_BATTERY = 'deep_battery';
export const OBJECT_RELAY_SPIRE = 'relay_spire';
export const OBJECT_REEF_BREAKWATER = 'reef_breakwater';
export const OBJECT_DEN = 'den';
export const OBJECT_LAIR = 'lair';
export type ObjectKind = 'player_base' | 'faction_base' | 'resource_field' | 'trench_throne' | 'coral_ward' | 'deep_battery' | 'relay_spire' | 'reef_breakwater' | 'den' | 'lair';

/** The three resources a field may carry (`map.objects.field_resource`). */
export const RESOURCE_ALLOY = 'alloy';
export const RESOURCE_BIOMASS = 'biomass';
export const RESOURCE_VOLATILES = 'volatiles';
export const RESOURCE_KINDS = ['alloy', 'biomass', 'volatiles'] as const;
export type ResourceKind = 'alloy' | 'biomass' | 'volatiles';

/** Marker shapes a tile can carry (arcade.md §4.9). */
export const MARKER_ARROW = 'arrow';
export const MARKER_CIRCLE = 'circle';
export type MarkerShape = 'arrow' | 'circle';

/** Win condition types, in the order arcade.md §4.5 lists them. */
export const WIN_KILL_ALL = 'kill_all';
export const WIN_SOV_SHARE = 'sov_share';
export const WIN_SOV_CELL = 'sov_cell';
export const WIN_MINE = 'mine';
export const WIN_KILL_CLASS = 'kill_class';
export const WIN_KILLS = 'kills';
export const WIN_SCAN_CLASS = 'scan_class';
export const WIN_DRILL = 'drill';
export const WIN_HOLD_STRUCTURES = 'hold_structures';
export const WIN_TYPES = ['kill_all', 'sov_share', 'sov_cell', 'mine', 'kill_class', 'kills', 'scan_class', 'drill', 'hold_structures'] as const;
export type WinType = 'kill_all' | 'sov_share' | 'sov_cell' | 'mine' | 'kill_class' | 'kills' | 'scan_class' | 'drill' | 'hold_structures';

/** Means values for a kill condition. */
export const MEANS_ANY = 'any';
export const MEANS_SHIP = 'ship';
export const MEANS_TORPEDO = 'torpedo';
export type Means = 'any' | 'ship' | 'torpedo';

/** The done check of a drill step (arcade.md §17.3, R1564). */
export const DRILL_DONE_ACK = 'ack';
export const DRILL_DONE_REACH = 'reach';
export const DRILL_DONE_BUILD = 'build';
export const DRILL_DONE_PING = 'ping';
export const DRILL_DONE_SINK = 'sink';
export const DRILL_DONE_LAUNCH = 'launch';
export const DRILL_DONE_TAKE = 'take';
export const DRILL_DONE_DETONATE = 'detonate';
export const DRILL_DONE_ANCHOR = 'anchor';
export const DRILL_DONE_HAUL = 'haul';
export const DRILL_DONE_HEAR = 'hear';
export const DRILL_DONE_HIDE = 'hide';
export const DRILL_DONE_PATROL = 'patrol';
export const DRILL_DONE_ESCORT = 'escort';
export const DRILL_DONE_RESTOCK = 'restock';
export const DRILL_DONE_MEND = 'mend';
export const DRILL_DONE_REARM = 'rearm';
export const DRILL_DONE_GOAL = 'goal';
export const DRILL_DONE_STOW = 'stow';
export const DRILL_DONE_FILL = 'fill';
export const DRILL_DONE_AMBUSH = 'ambush';
export const DRILL_DONE_STERN = 'stern';
export const DRILL_DONE_SIGNATURE = 'signature';
export const DRILL_DONE_BRACED = 'braced';
export const DRILL_DONE_OUTFLANK = 'outflank';
export const DRILL_DONE_GUARD = 'guard';
export const DRILL_DONE_KINDS = ['ack', 'reach', 'build', 'ping', 'sink', 'launch', 'take', 'detonate', 'anchor', 'haul', 'hear', 'hide', 'patrol', 'escort', 'restock', 'mend', 'rearm', 'goal', 'stow', 'fill', 'ambush', 'stern', 'signature', 'braced', 'outflank', 'guard'] as const;
export type DrillDone = 'ack' | 'reach' | 'build' | 'ping' | 'sink' | 'launch' | 'take' | 'detonate' | 'anchor' | 'haul' | 'hear' | 'hide' | 'patrol' | 'escort' | 'restock' | 'mend' | 'rearm' | 'goal' | 'stow' | 'fill' | 'ambush' | 'stern' | 'signature' | 'braced' | 'outflank' | 'guard';

/** What the bot seats do during a drill step (arcade.md §17.4). */
export const DRILL_BOTS_STILL = 'still';
export const DRILL_BOTS_DEFEND = 'defend';
export const DRILL_BOTS_FREE = 'free';
export const DRILL_BOTS_WORK = 'work';
export const DRILL_BOTS_ASSAULT = 'assault';
export const DRILL_BOTS_CRUISE = 'cruise';
export const DRILL_BOT_MODES = ['still', 'defend', 'free', 'work', 'assault', 'cruise'] as const;
export type DrillBots = 'still' | 'defend' | 'free' | 'work' | 'assault' | 'cruise';

/** The chrome elements a drill step may highlight (arcade.md §17.2). */
export const DRILL_CHROME_BANK = 'bank';
export const DRILL_CHROME_CLOCK = 'clock';
export const DRILL_CHROME_OBJECTIVE = 'objective';
export const DRILL_CHROME_BUILD_PANE = 'build_pane';
export const DRILL_CHROME_SELECTION = 'selection';
export const DRILL_CHROME_ORDERS = 'orders';
export const DRILL_CHROME_BASE_FIRE = 'base_fire';
export const DRILL_CHROME_GROUPS = 'groups';
export const DRILL_CHROME_SIGNATURE_PRESS = 'signature_press';
export const DRILL_CHROME = ['bank', 'clock', 'objective', 'build_pane', 'selection', 'orders', 'base_fire', 'groups', 'signature_press'] as const;
export type DrillChrome = 'bank' | 'clock' | 'objective' | 'build_pane' | 'selection' | 'orders' | 'base_fire' | 'groups' | 'signature_press';

/** The step reset of a drill step (arcade.md §17.3, R2421). */
export const DRILL_RESET_HEARD = 'heard';
export const DRILL_RESET_ARRIVED = 'arrived';
export const DRILL_RESET_SUNK = 'sunk';
export const DRILL_RESET_LOST = 'lost';
export const DRILL_RESET_WASTED = 'wasted';
export const DRILL_RESETS = ['heard', 'arrived', 'sunk', 'lost', 'wasted'] as const;
export type DrillReset = 'heard' | 'arrived' | 'sunk' | 'lost' | 'wasted';

/** The kind of a map's stated symmetry (arcade.md §4.10, R2016). */
export const SYM_ROT180 = 'rot180';
export const SYM_D2 = 'd2';
export const SYM_ROT120 = 'rot120';
export const SYMMETRY_KINDS = ['rot180', 'd2', 'rot120'] as const;
export type SymmetryKind = 'rot180' | 'd2' | 'rot120';

/** The point of a stated centre cell a symmetry turns about (arcade.md
 * §4.10, R2016). */
export const SYM_AT_CELL = 'cell';
export const SYM_AT_VERTEX_N = 'vertex_n';
export const SYM_AT_VERTEX_S = 'vertex_s';
export const SYM_AT_EDGE_E = 'edge_e';
export const SYM_AT_EDGE_SE = 'edge_se';
export const SYM_AT_EDGE_SW = 'edge_sw';
export const SYMMETRY_ATS = ['cell', 'vertex_n', 'vertex_s', 'edge_e', 'edge_se', 'edge_sw'] as const;
export type SymmetryAt = 'cell' | 'vertex_n' | 'vertex_s' | 'edge_e' | 'edge_se' | 'edge_sw';

/** The pirate brotherhoods a den object names (arcade.md §20, R3216), in
 * the order of [[npc.brotherhoods]]. */
export const BROTHERHOOD_LANTERN_KIN = 'lantern_kin';
export const BROTHERHOOD_SALTJAW = 'saltjaw';
export const BROTHERHOOD_NINEFATHOM = 'ninefathom';
export const BROTHERHOOD_GRAPNEL_COMPANY = 'grapnel_company';
export const BROTHERHOOD_UNDERTOW = 'undertow';
export const BROTHERHOODS = ['lantern_kin', 'saltjaw', 'ninefathom', 'grapnel_company', 'undertow'] as const;
export type BrotherhoodWord = 'lantern_kin' | 'saltjaw' | 'ninefathom' | 'grapnel_company' | 'undertow';

/** The monster kinds a lair object names (arcade.md §20, R3216), in the
 * order of [[npc.monsters]]. */
export const MONSTER_GULPER = 'gulper';
export const MONSTER_MURKHIDE = 'murkhide';
export const MONSTER_QUILLBACK = 'quillback';
export const MONSTER_THRUMMER = 'thrummer';
export const MONSTER_MYRIAD = 'myriad';
export const MONSTER_KINDS = ['gulper', 'murkhide', 'quillback', 'thrummer', 'myriad'] as const;
export type MonsterKind = 'gulper' | 'murkhide' | 'quillback' | 'thrummer' | 'myriad';

/** The map categories whose active maps state `symmetry` (arcade.md
 * §4.10, R2016). */
export const SYMMETRY_REQUIRED_CATEGORIES: readonly string[] = ['sovereignty', 'ffa', 'quick'];

/** The player classes a map file must not name yet (v2-the-deep.md
 * §17.6): a match plays none of their rules. */
export const HELD_SHIP_CLASSES: readonly string[] = [];

/** The class cap of each capped player class (ships.toml `cap`, R3218):
 * a seat of a match holds at most this many hulls of the class
 * (`map.fleets.class_cap`). */
export const SHIP_CLASS_CAP: Readonly<Record<string, number>> = { 'Commodore': 1 };

/** The sfx trigger vocabulary of audio.md §4.3-§4.5 and the wired
 * half of §4.8 (audio.toml). A theme names its clip files after these
 * ids. The menu shell's own `sfx-menu-*` cues are the client's
 * furniture and are NOT here. */
export const SFX_IDS = [
  // §4.3 Core set
  'sfx-torpedo-fire',
  'sfx-torpedo-explode',
  'sfx-fleet-fight',
  'sfx-fleet-sink',
  'sfx-sonar-sweep',
  'sfx-scan',
  'sfx-beacon-anchor',
  'sfx-sentinel-anchor',
  'sfx-tender-anchor',
  'sfx-beacon-pulse',
  'sfx-sov1-taken',
  'sfx-sov2-taken',
  'sfx-queue-complete',
  'sfx-auction-sold',
  'sfx-fleet-returned',
  'sfx-quake-stage1',
  'sfx-quake-stage2',
  'sfx-quake-settle',
  // §4.4 Extended set — contacts and combat
  'sfx-contact-new',
  'sfx-contact-lost',
  'sfx-torpedo-incoming',
  'sfx-under-attack',
  'sfx-taking-damage',
  'sfx-fleet-hit',
  'sfx-reload-complete',
  'sfx-we-are-detected',
  'sfx-enemy-destroyed',
  'sfx-torpedo-repair',
  'sfx-guard-kill',
  'sfx-torpor-burst',
  'sfx-ambush-shot',
  'sfx-round-select',
  'sfx-shields-down',
  'sfx-magazine-empty',
  'sfx-signature-order',
  'sfx-signature-ready',
  'sfx-contact-broken',
  // §4.4 Extended set — economy
  'sfx-build-queued',
  'sfx-ship-launched',
  'sfx-research-complete',
  'sfx-tech-unlocked',
  'sfx-insufficient-resources',
  'sfx-order-refused',
  'sfx-requirement-missing',
  'sfx-deposit-depleted',
  'sfx-cargo-delivered',
  'sfx-blueprint-acquired',
  'sfx-hull-repaired',
  'sfx-redraft-done',
  'sfx-blueprint-masterwork',
  // §4.4 Extended set — orders
  'sfx-fleet-departs',
  'sfx-order-ack',
  'sfx-course-plotted',
  'sfx-fleet-fatigued',
  'sfx-expedition-departed',
  'sfx-expedition-returned',
  // §4.4 Extended set — territory and season
  'sfx-territory-lost',
  'sfx-siege-defend',
  'sfx-siege-attack',
  'sfx-object-lost',
  'sfx-object-held',
  'sfx-siege-failed',
  'sfx-beacon-destroyed',
  'sfx-beacon-hostile',
  'sfx-truce-ended',
  'sfx-cataclysm',
  'sfx-map-arrival',
  'sfx-quake-final',
  // §4.4 Extended set — captains
  'sfx-captain-recruited',
  'sfx-captain-promoted',
  'sfx-captain-lost',
  'sfx-captain-taken',
  // §4.4 Extended set — clan, Honour and the clock
  'sfx-clan-hail',
  'sfx-squad-ping',
  'sfx-honour-awarded',
  'sfx-day-night',
  'sfx-truce-tick',
  // §4.4 Extended set — achievements
  'sfx-achievement',
  'sfx-trial-passed',
  // §4.8 Arcade set — lobby and countdown
  'sfx-arcade-queue-join',
  'sfx-arcade-queue-leave',
  'sfx-arcade-match-found',
  'sfx-arcade-seat-loaded',
  'sfx-arcade-count',
  'sfx-arcade-fight',
  // §4.8 Arcade set — match
  'sfx-arcade-buy',
  'sfx-arcade-spawn',
  'sfx-arcade-base-fire',
  'sfx-arcade-kill',
  'sfx-arcade-sov-lead',
  'sfx-arcade-sov-behind',
  'sfx-arcade-objective',
  'sfx-arcade-hold',
  'sfx-arcade-hold-broken',
  'sfx-arcade-capture-start',
  'sfx-arcade-capture-threat',
  'sfx-arcade-capture-broken',
  'sfx-arcade-step-reset',
  'sfx-arcade-surrender',
  'sfx-arcade-last-minute',
  'sfx-arcade-clock-tick',
  'sfx-arcade-win',
  'sfx-arcade-lose',
  'sfx-arcade-draw',
  'sfx-arcade-verdict',
  // §4.9 Casino set
  'sfx-casino-chip',
  'sfx-casino-deal',
  'sfx-casino-wheel',
  'sfx-casino-ball',
  'sfx-casino-reel-stop',
  'sfx-casino-win',
  'sfx-casino-payout',
  'sfx-casino-jackpot',
  'sfx-casino-lose',
  'sfx-casino-your-turn',
  'sfx-casino-seat',
  'sfx-club-payout',
] as const;

/** One registered trigger id. */
export type KnownSfxId = (typeof SFX_IDS)[number];

// The event ids the client code tests by name (events.toml `ts_const`).
// The value is `Notice.event` on the wire.
export const EVENT_CLUB_PAID = 'club_paid';
export const EVENT_REPLAY_GONE = 'replay_gone';

/** The push classes (events.toml `[events.push]`, player-reach.md §3.1).
 * The settings screen shows one switch per class, in this order. The
 * value is the word the meta server stores. */
/**
 * A siege at an object of the faction: a pledge came in (siege_defend,
 * siege_attack), or the siege windows of the world day open soon
 * (siege_window_soon), or a squad mate calls a rally at an object
 * (squad_rally, R2200). The one class that may send in the quiet hours,
 * when the player sets the night switch (player-reach.md §3.2).
 */
export const PUSH_SIEGE = 'siege';
/**
 * Enemy fire near the hold of the player (hold_under_fire).
 */
export const PUSH_HOLD = 'hold';
/**
 * A fleet of the player sank (fleet_lost, fleet_lost_cargo, fleet_lost_by).
 */
export const PUSH_FLEET = 'fleet';
/**
 * The build queue or the laboratory finished a job and waits
 * (queue_done, research_done).
 */
export const PUSH_ECONOMY = 'economy';
/**
 * The clan cup: the check-in of a cup day opens soon. The meta server sends
 * it itself, once per cup, to the members of every drawn team, players and
 * reserves (clan-cup.md §6, R1629). No event row names the class.
 */
export const PUSH_CUP = 'cup';
/**
 * The NPC side: a pirate group took a hull of the player as its target
 * (pirate_contact), a monster moves to a hull of the player
 * (monster_heard), or pirates board or took a support hull of the player
 * (boarding_started, hull_taken). v2-the-deep.md §8, R2622, R2721.
 */
export const PUSH_PIRATES = 'pirates';
/**
 * The sea mines: a hull of the player set off a sea mine (sea_mine_struck),
 * a sea mine that the player laid went off (sea_mine_tripped), or a sea
 * object of the player left the map without an act of the player
 * (sea_object_lost). v2-the-deep.md §11.2, §14.7, §15.4, R2653, R2887.
 */
export const PUSH_SEA_MINES = 'sea_mines';
export const PUSH_CLASSES = [PUSH_SIEGE, PUSH_HOLD, PUSH_FLEET, PUSH_ECONOMY, PUSH_CUP, PUSH_PIRATES, PUSH_SEA_MINES] as const;
/** One push class. */
export type PushClass = (typeof PUSH_CLASSES)[number];
/** The feature flag of a push class behind one (R2637). */
export const PUSH_CLASS_FEATURES: Partial<Record<PushClass, string>> = { pirates: 'pirates', sea_mines: 'sapper' };

/** The clan post classes (events.toml `[events.discord]`, player-reach.md §3.9).
 * The DISCORD card shows one switch per class, in this order. The value
 * is the word the meta server stores. */
/**
 * A siege at an object of the faction of the clan: a pledge came in
 * (siege_defend, siege_attack), or the siege windows of the world day
 * open soon (siege_window_soon). The window of a siege closed: an object
 * fell, held or was mauled, as the owner or as the attacker sees it
 * (object_fallen, object_held, object_mauled, as clan-only alerts of the
 * `clan_post` routing field, R1643).
 */
export const DISCORD_SIEGE = 'siege';
/**
 * A member joins the clan or leaves it (member_joined, member_left), a
 * commander applies to the clan (application_received), a member becomes
 * an officer or an officer becomes a member (member_promoted,
 * member_demoted), or the clan has a new owner (owner_changed, R1640).
 */
export const DISCORD_MEMBERS = 'members';
/**
 * The clan finished a Clan War season in one world (clanwar_result), or
 * the clan is the War Champion of a Clan War and a trench carries its
 * name (trench_granted). In a clanwar world where the clan holds a war
 * banner, the founding truce ends soon and ends (truce_ends_soon,
 * truce_ended), and seaquake stages 1 and 2 open soon and open
 * (quake_stage_1_soon, quake_stage_1, quake_stage_2_soon, quake_stage_2;
 * R1640). The clan won a Clan War division and earned the division badge
 * (clanwar_division, R1643).
 */
export const DISCORD_CLANWAR = 'clanwar';
/**
 * The registration notices of the Clan War and of the clan cup (R1640):
 * the registration opens, it closes soon, it closed with the outcome of
 * the clan, the event starts soon, and the event starts.
 */
export const DISCORD_REGISTRATION = 'registration';
/**
 * The milestones of the clan (R1643): a decided clan cup match, won or
 * lost, and the cup title or the runner-up place after the final
 * (cup_match, cup_title, cup_runner_up); the Clan Honour of the clan
 * passes a multiple of [meta.discord] honour_step (honour_step); and a
 * member reaches a higher arcade ladder tier (ladder_tier).
 */
export const DISCORD_MILESTONES = 'milestones';
export const DISCORD_CLASSES = [DISCORD_SIEGE, DISCORD_MEMBERS, DISCORD_CLANWAR, DISCORD_REGISTRATION, DISCORD_MILESTONES] as const;
/** One clan post class. */
export type DiscordClass = (typeof DISCORD_CLASSES)[number];
/** The INBOX buckets (events.toml `[events.inbox]`, inbox.md §2). The
 * filter row shows one bucket per segment, in this order. The value is
 * the word of `InboxMsg.bucket`. */
/**
 * What the empire lost: a fleet and its captain (fleet_lost,
 * fleet_lost_cargo, fleet_lost_by), a hull broken for room (room_made) and
 * cargo that spilled (cargo_spilled).
 */
export const INBOX_BUCKET_LOSS = 'loss';
/**
 * The fighting of the empire: an enemy hull sunk with its salvage
 * (fleet_killed), the two ends of a torpedo (torpedo_hit,
 * torpedo_struck), a window battle (battle_report), a captain taken
 * prisoner (captain_taken), and the NPC side (pirate_contact,
 * monster_heard, fragment_found, boarding_started, hull_taken,
 * boarding_stopped, monster_sunk), and the sea objects (sea_mine_struck,
 * sea_mine_tripped, sea_object_lost).
 */
export const INBOX_BUCKET_WAR = 'war';
/**
 * The hold and the water of the empire: fire near the hold
 * (hold_under_fire), water lost (territory_lost), a siege (siege_defend),
 * the hold moved (hold_moved) and an object of the side that fell, was mauled or held
 * (object_fallen, object_mauled, object_held).
 */
export const INBOX_BUCKET_HOLD = 'hold';
/**
 * A dive that surfaced (expedition_ok, expedition_core, expedition_hurt,
 * expedition_hurt_core).
 */
export const INBOX_BUCKET_DIVE = 'dive';
/**
 * A sale on the market (market_sold_isotopes, market_sold_print) and the
 * club share of the casino (club_paid, R2144).
 */
export const INBOX_BUCKET_TRADE = 'trade';
/**
 * The season and the commander: the season report (season_closed), the
 * pickup squad mates (pickup_squad), an achievement grade (achievement),
 * a redraft that improved a master or was discarded (redraft_done,
 * R1884) and the featured object of the next world day (featured_next,
 * R3272).
 */
export const INBOX_BUCKET_WORLD = 'world';
export const INBOX_BUCKETS = [INBOX_BUCKET_LOSS, INBOX_BUCKET_WAR, INBOX_BUCKET_HOLD, INBOX_BUCKET_DIVE, INBOX_BUCKET_TRADE, INBOX_BUCKET_WORLD] as const;
/** One INBOX bucket. */
export type InboxBucket = (typeof INBOX_BUCKETS)[number];
/** The longest webhook URL the meta server stores (meta.toml [meta.discord] url_max_len). */
export const DISCORD_URL_MAX = 256;
/** The most digits of a Discord role id (meta.toml [meta.discord] role_id_max_digits, R1642). */
export const DISCORD_ROLE_ID_MAX = 20;

// The game modes (modes.toml). The same words are the ticket's
// numbering on the game server and the `mode` column of the meta
// database.
export const MODE_CORE = 'core';
export const MODE_EVENT = 'event';
export const MODE_CLANWAR = 'clanwar';
export const MODE_ARCADE = 'arcade';
export const MODE_CUSTOM = 'custom';
export type ModeName = 'core' | 'event' | 'clanwar' | 'arcade' | 'custom';

// The entitlement key catalog (entitlements.toml). The account's own
// set arrives in `Me.entitlements`; these are the names a panel gates a
// card on. The bit each key occupies is the server's business and is
// not here.
export const KEY_SUPPORTER = 'supporter';
export const KEY_PREMIUM = 'premium';
export const KEY_CAPTAIN_DESIGNER = 'captain_designer';
export const KEY_FLEET_DESIGNER = 'fleet_designer';
export const KEY_UI_THEMES = 'ui_themes';
export const KEY_CHRONICLE = 'chronicle';
export const KEY_EXTENDED_QUEUE = 'extended_queue';
export const KEY_DREDGER_BRAIN = 'dredger_brain';
export const KEY_AUTO_REBUILD = 'auto_rebuild';
export const KEY_SABBATICAL = 'sabbatical';
export const KEY_VARIANT_WORLDS = 'variant_worlds';
export const KEY_THEME_MAKER = 'theme_maker';
export const KEY_MAP_PUBLISHER = 'map_publisher';
export const KEY_FREE_TEXT = 'free_text';
export const KEY_MODS = 'mods';
export const KEY_EVENT = 'event';
export const KEY_CLANWAR = 'clanwar';
export const KEY_ADMIN = 'admin';
export type EntitlementKey = 'supporter' | 'premium' | 'captain_designer' | 'fleet_designer' | 'ui_themes' | 'chronicle' | 'extended_queue' | 'dredger_brain' | 'auto_rebuild' | 'sabbatical' | 'variant_worlds' | 'theme_maker' | 'map_publisher' | 'free_text' | 'mods' | 'event' | 'clanwar' | 'admin';

// The meta server's API error codes (api.toml). Every failure answers
// with `{"error": {"code", "message"}}`; the code is the machine token a
// screen switches on, and the message is never parsed. `net/api.ts`
// re-exports the ones a screen names, and keeps its own two codes for a
// listener that answered no JSON at all.
export const CODE_BAD_REQUEST = 'bad_request';
export const CODE_UNAUTHORIZED = 'unauthorized';
export const CODE_FORBIDDEN = 'forbidden';
export const CODE_NOT_FOUND = 'not_found';
export const CODE_CONFLICT = 'conflict';
export const CODE_RATE_LIMITED = 'rate_limited';
export const CODE_INTERNAL = 'internal';
export const CODE_NOT_IMPLEMENTED = 'not_implemented';
export const CODE_READ_ONLY = 'read_only';
export const CODE_UNSUPPORTED_MEDIA_TYPE = 'unsupported_media_type';
export const CODE_INVALID_LOGIN = 'invalid_credentials';
export const CODE_EMAIL_TAKEN = 'email_taken';
export const CODE_INVALID_EMAIL = 'invalid_email';
export const CODE_WEAK_PASSWORD = 'weak_password';
export const CODE_ALREADY_LINKED = 'already_registered';
export const CODE_NAME_TAKEN = 'name_taken';
export const CODE_INVALID_NAME = 'invalid_name';
export const CODE_RENAME_SPENT = 'rename_spent';
export const CODE_ENTITLEMENT = 'entitlement_required';
export const CODE_MODE_NOT_OPEN = 'mode_not_open';
export const CODE_NO_WORLD = 'no_world_available';
export const CODE_NO_BANNER_SEAT = 'no_banner_seat';
export const CODE_WORLD_UNREACHABLE = 'world_unreachable';
export const CODE_WORLD_NOT_JOINABLE = 'world_not_joinable';
export const CODE_MODE_SEAT_HELD = 'mode_seat_held';
export const CODE_FIRST_JOIN_HELD = 'first_join_held';
export const CODE_SEASON_ROLLING = 'season_rolling';
export const CODE_SHOP_DISABLED = 'shop_disabled';
export const CODE_REGISTRATION_REQUIRED = 'registration_required';
export const CODE_PLAN_UNKNOWN = 'plan_unknown';
export const CODE_NO_AGENT = 'no_host_agent';
export const CODE_AGENT_FAILED = 'host_agent_failed';
export const CODE_RELEASE_BUSY = 'release_busy';
export const CODE_RELEASE_NOT_STAGED = 'release_not_staged';
export const CODE_MAINTENANCE = 'maintenance';
export const CODE_NO_CLAN = 'no_clan';
export const CODE_IN_CLAN = 'already_in_clan';
export const CODE_CLAN_TAKEN = 'clan_taken';
export const CODE_APPLIED = 'already_applied';
export const CODE_CLAN_FULL = 'clan_full';
export const CODE_LAST_OWNER = 'owner_must_hand_over';
export const CODE_CLAN_CLOSED = 'clan_closed';
export const CODE_REGISTRATION_CLOSED = 'registration_closed';
export const CODE_NO_CLANWAR_RUN = 'no_clanwar_run';
export const CODE_NO_BANNER = 'no_banner';
export const CODE_CUP_CLOSED = 'cup_closed';
export const CODE_CUP_FULL = 'cup_full';
export const CODE_CUP_CHECKIN_CLOSED = 'cup_checkin_closed';
export const CODE_CUP_NOT_ENTERED = 'cup_not_entered';
export const CODE_CUP_OVER = 'cup_over';
export const CODE_NOT_ASSIGNED = 'not_assigned';
export const CODE_NO_CALENDAR = 'no_calendar';
export const CODE_POOLED_RUN = 'pooled_run';
export const CODE_IDENTITY_TAKEN = 'identity_taken';
export const CODE_SIGN_IN_STATE = 'sign_in_state';
export const CODE_SIGN_IN_REFUSED = 'sign_in_refused';
export const CODE_SIGN_IN_METHOD_OFF = 'sign_in_method_off';
export const CODE_MAIL_OFF = 'mail_off';
export const CODE_RESET_EXPIRED = 'reset_expired';
export const CODE_IMPERSONATION_REFUSED = 'impersonation_refused';
export const CODE_IMP_CODE_EXPIRED = 'imp_code_expired';
export const CODE_REAUTH_REQUIRED = 'reauth_required';
export const CODE_INVALID_THEME = 'invalid_theme';
export const CODE_THEME_TOO_LARGE = 'theme_too_large';
export const CODE_UNKNOWN_CODE = 'unknown_code';
export const CODE_INVALID_CAPTAIN_LOOKS = 'invalid_captain_looks';
export const CODE_INVALID_FLEET_NAMES = 'invalid_fleet_names';
export const CODE_INVALID_FLEET_LIVERY = 'invalid_fleet_livery';
export const CODE_REPLAY_NOT_FOUND = 'replay_not_found';
export const CODE_REPLAY_NOT_LIVE = 'replay_not_live';
export const CODE_TOO_MANY_REPLAY_LOOKUPS = 'too_many_replay_lookups';
export const CODE_REPLAY_CODE_MALFORMED = 'replay_code_malformed';
export const CODE_REPLAY_CODE_TAKEN = 'replay_code_taken';
export const CODE_MATCH_REPORT_PHASE = 'match_report_phase';
export const CODE_EFFECT_SLOT_UNKNOWN = 'effect_slot_unknown';
export const CODE_EFFECT_NOT_GRANTED = 'effect_not_granted';
export const CODE_EFFECT_SLOT_REFUSED = 'effect_slot_refused';
export const CODE_BATCH_SEEN = 'batch_seen';
export const CODE_SEASON_SEEN = 'season_seen';
export const CODE_ACHIEVEMENT_UNKNOWN = 'achievement_unknown';
export const CODE_RECOGNITION_UNKNOWN = 'recognition_unknown';
export const CODE_SHARD_UNKNOWN = 'shard_unknown';
export const CODE_PROGRESS_UNAVAILABLE = 'progress_unavailable';
export const CODE_PUSH_OFF = 'push_off';
export const CODE_PUSH_INVALID = 'push_invalid';
export const CODE_DISCORD_INVALID = 'discord_invalid';
export const CODE_DISCORD_NONE = 'discord_none';
export const CODE_RELAY_NONE = 'relay_none';
export const CODE_HOURS_INVALID = 'hours_invalid';
export const CODE_NOT_PICKUP_MATE = 'not_pickup_mate';
export const CODE_INVITEE_IN_CLAN = 'invitee_in_clan';
export const CODE_ALREADY_INVITED = 'already_invited';
export const CODE_INVITE_CAP = 'invite_cap';
export const CODE_INVITE_EXPIRED = 'invite_expired';
export const CODE_DELETE_CONFIRM = 'delete_confirm';
export const CODE_SUBSCRIPTION_ACTIVE = 'subscription_active';
export const CODE_WORLD_NOT_RETIRED = 'world_not_retired';
export const CODE_WORLD_RUN_LIVE = 'world_run_live';
export const CODE_ARCADE_WORLD = 'arcade_world';
export const CODE_CHAT_MUTED = 'chat_muted';
export const CODE_BLOCK_CAP = 'block_cap';
export const CODE_REPORT_CLOSED = 'report_closed';
export const CODE_FAIRPLAY_CLOSED = 'fairplay_closed';
export const CODE_SLOT_TAKEN = 'slot_taken';
export const CODE_MAP_INVALID = 'map_invalid';
export const CODE_MAP_TOO_LARGE = 'map_too_large';
export const CODE_SLOT_RANGE = 'slot_range';
export const CODE_HOSTING_BUDGET_SPENT = 'hosting_budget_spent';
export const CODE_NO_INSTANCE_FREE = 'no_instance_free';
export const CODE_EVENT_TOO_CLOSE = 'event_too_close';
export const CODE_LOBBY_FULL = 'lobby_full';
export const CODE_NOT_LOBBY_HOST = 'not_lobby_host';
export const CODE_LOBBY_NOT_FOUND = 'lobby_not_found';
export const CODE_LOBBY_NOT_OPEN = 'lobby_not_open';
export const CODE_LOBBY_HELD = 'lobby_held';
export const CODE_ROSTER_RULE = 'roster_rule';
export const CODE_SETTING_RANGE = 'setting_range';
export const CODE_SETTING_LOCKED = 'setting_locked';

/** The map spawn animation, played on the first Hello and on a season
 * roll's re-Hello (labels.toml). ORDER IS THE CYCLE ORDER: SETTINGS steps
 * to the next entry and prints "3 of 5". */
export const SPAWN_STYLES = [
  'sonar-bloom',
  'caustic-sweep',
  'cell-ripple',
  'seaquake-settle',
  'deep-rise',
  'stacked-deck',
] as const;

/** The torpedo-in-flight look (labels.toml), the same way. */
export const TORPEDO_STYLES = [
  'terminal-burn',
  'two-stage',
  'spindle',
  'ribbon',
  'bubble-wake',
] as const;

/** How a damage figure appears at a struck hull and goes (labels.toml,
 * mockup round thirty, R538), the same way. */
export const DAMAGE_STYLES = [
  'rise',
  'pop-and-drop',
  'fly-out',
  'stack',
  'leader-pin',
] as const;

/** Which reading of the sea chart the map draws (labels.toml, mockup
 * rounds 28, 29 and 31, R548), the same way. `off` draws no piece at
 * all; `modern` is the default. */
export const CHART_READINGS = [
  'off',
  'historical',
  'modern',
] as const;

/** The chart convention every hold is drawn as (labels.toml, mockup
 * round thirty-two, R633), the same way. The reading of the symbol is
 * the CHART setting's; the first entry is the default. */
export const HOLD_SYMBOLS = [
  'beacon-post',
  'cartouche',
  'signal-station',
  'rose',
  'harbour',
  'dome',
] as const;

/** The look lists of a World and a Marks theme (labels.toml, themes.md
 * §3). ORDER IS THE CYCLE ORDER, as above, and position 0 is the AUTO
 * value (R822). */
export const NOTICE_STYLES = [
  'drain',
  'ledger',
  'split-lane',
  'under-clock',
  'ribbon',
  'bare-type',
] as const;
export const DOCK_STYLES = [
  'magnet',
  'hard-dock',
  'cascade',
  'pressure-rise',
  'surface-wake',
] as const;
export const BANNER_STYLES = [
  'abyss',
  'sonar-type',
  'depth-gauge',
  'horizon-split',
  'echo-count',
  'season-bar',
] as const;
export const TERRITORY_STYLES = [
  'default',
  'hex',
] as const;
export const TERRAIN_STYLES = [
  'default',
  'form',
  'material',
] as const;
export const BEAM_STYLES = [
  'breathing-line',
  'pip-cable',
  'marching-dash',
  'cable',
  'gathering-ripple',
  'twisted-pair',
] as const;
export const AURA_STYLES = [
  'regen-wave',
  'coverage-disc',
  'bubble',
  'lattice-ring',
  'hex-cells',
  'spokes',
  'mesh',
] as const;
export const SHIP_STYLES = [
  'default',
  'rungs',
  'pips',
  'arcs',
  'chevrons',
  'plates',
  'tails',
  'teeth',
  'arrows',
  'by-name',
  'insignia',
  'filigree',
] as const;
export const HULL_STYLES = [
  'follow',
  'default',
  'rungs',
  'pips',
  'arcs',
  'chevrons',
  'plates',
  'tails',
  'teeth',
  'arrows',
  'by-name',
  'insignia',
  'filigree',
] as const;
export const OWN_FLEET_STYLES = [
  'white-core',
  'caret-pair',
  'diamond-cage',
  'viewfinder',
  'command-pennant',
  'hex-frame',
] as const;
export const SQUAD_FLEET_STYLES = [
  'trailing-dot',
  'hollow-hull',
  'tint-only',
  'under-dash',
  'slot-numeral',
  'faint-lozenge',
] as const;
export const SHIELD_STYLES = [
  'stack',
  'cap',
  'sheath',
  'ticks',
  'lanes',
  'fade',
] as const;
export const DIGIT_STYLES = [
  'mono',
  'seg-stem',
  'seg-flag',
  'seg-flag-foot',
  'seg-round',
] as const;
export const OWN_HOLD_STYLES = [
  'ground-plate',
  'beam-column',
  'crown-chevrons',
  'cross-rays',
  'breathing-lozenge',
  'landing-pillars',
] as const;
export const SQUAD_HOLD_STYLES = [
  'half-plate',
  'no-mark',
  'single-chevron',
  'waterline-ticks',
  'short-column',
  'small-lozenge',
] as const;
export const DEPOSIT_STYLES = [
  'drain',
  'strike',
  'gauge',
  'ghost',
  'tag',
  'tally',
] as const;
export const STRUCTURE_SYMBOLS = [
  'default',
  'pictogram',
  'heraldic',
  'silhouette',
  'blocks',
  'echo',
  'gauge-ring',
  'tile',
  'letters',
  'buoys',
] as const;
export const SHOT_MELEE_STYLES = [
  'burst-fire',
  'point-blank-flash',
  'stitch',
  'broadside',
  'spread-cone',
  'spear-and-case',
  'lunge',
  'piston',
  'arc-discharge',
  'sweep',
] as const;
export const SHOT_ARTILLERY_STYLES = [
  'track-arc',
  'siege-salvo',
  'shell-lob',
  'star-shell',
  'rail-dart',
  'sabot',
  'heavy-round',
  'fuse-spark',
  'muzzle-bloom',
  'beam-pulse',
] as const;
export const SHOT_SENTINEL_STYLES = [
  'charge-and-snap',
  'sentry-pulses',
  'bulwark-lattice',
  'interceptor-thread',
  'wind-up',
  'tripwire',
  'drum-fire',
  'stepped-bolt',
  'mine-drift',
  'sea-bed-kick',
] as const;
export const HIT_SHIELD_STYLES = [
  'interference',
  'ring-flare',
  'ring-ripple',
  'plate-lattice',
  'growing-crack',
  'low-flicker',
  'drain-shift',
  'dome-sheen',
  'rim-sparks',
  'rim-arcs',
] as const;
export const HIT_HULL_STYLES = [
  'through-and-through',
  'fragment-fan',
  'spark-shower',
  'smoke-puffs',
  'vent-jet',
  'list',
  'outline-flicker',
  'bubble-fire',
  'inner-fire',
  'short-circuit',
] as const;
export const HIT_SINK_STYLES = [
  'implosion',
  'spiral-descent',
  'bubble-collapse',
  'break-in-two',
  'stern-first',
  'silt-burst',
  'wreck-print',
  'last-light',
  'ink-ghost',
  'steam-shroud',
] as const;
export const GATHER_STYLES = [
  'chips-lift',
  'drag-loop',
  'cutter-head',
  'silt-bloom',
  'keel-glow',
  'grab-beats',
  'hold-ring',
  'work-lights',
  'drain-pulse',
  'spiral-bit',
] as const;
export const ANCHOR_STYLES = [
  'corner-brackets',
  'buoy-countdown',
  'anchor-ring',
  'cable-fluke',
  'four-legs',
  'tick-dial',
  'hex-pin',
  'chart-anchor',
  'ground-line',
  'bubble-column',
  'ring-teeth',
] as const;
export const MAP_MARKER_STYLES = [
  'pulse-ring',
  'map-pin',
  'pennant',
  'pencil',
  'flare-column',
] as const;
export const HOLD_FIRE_STYLES = [
  'safety-lock',
  'safed-guns',
  'restricted-line',
  'clamp',
  'hatch-shimmer',
] as const;
export const RELOAD_STYLES = [
  'clock-digit',
  'tube-gauge',
  'sweep-segments',
  'loading-slide',
  'countdown-tag',
  'round-label',
  'pip-row',
  'arc-bar',
] as const;
export const DETONATION_STYLES = [
  'tide-front',
  'fireball-rim',
  'pressure-shell',
  'cavitation-collapse',
  'shattered-disc',
  'sonar-return',
  'depth-charge-boil',
  'hex-stamp',
  'spore-drift',
  'drag-particles',
] as const;
export const FACTION_SITE_STYLES = [
  'charge',
  'off',
] as const;
export const FACTION_HULL_STYLES = [
  'pip',
  'off',
] as const;
export const FACTION_EDGE_STYLES = [
  'dash',
  'off',
  'limit',
] as const;
export const FACTION_KEY_STYLES = [
  'key',
  'off',
] as const;
export const DEPLOYABLE_STYLES = [
  'terrain-kin',
  'danger-lines',
  'sidescan-returns',
  'blueprint-section',
  'obstacle-symbols',
  'hazard-stripes',
  'light-rhythms',
  'hardware',
  'acoustic-shapes',
  'effect-fields',
] as const;
export const SEA_MINE_STYLES = [
  'overprint-restricted',
  'horned-sphere',
  'drogue-lines',
  'drums-and-grilles',
  'seed-burrs',
  'side-profile',
  'copperplate',
  'danger-circle',
  'foul-ground',
  'living-motion',
] as const;
export const SEA_MINE_DETONATION_STYLES = [
  'reveal-ping',
  'water-column',
  'bubble-pulse',
  'foam-crown',
  'chart-stamp',
  'horn-burst',
  'pip-fuse',
  'spray-star',
] as const;
export const SEA_MINE_LAY_STYLES = [
  'countdown-ring',
  'plain-drop',
  'stern-slide',
  'chute-flash',
  'swing-cradle',
  'seed-line',
  'ripple',
  'safe-bubble',
] as const;
export const DEPLOYABLE_LAY_STYLES = [
  'carpet-unroll',
  'plain-set',
  'reel-pays-out',
  'fold-opens',
  'stake-driven',
  'blueprint-print',
  'crane-lowers',
  'splash-rings',
] as const;
export const SEA_MINE_COLLECT_STYLES = [
  'ping-and-pull',
  'closing-ring',
  'tractor-beams',
  'keel-squeeze',
  'clock-rim',
  'hex-sweep',
  'ledger-tally',
  'disarm-first',
] as const;
export const DEPLOYABLE_COLLECT_STYLES = [
  'blueprint-drain',
  'straight-haul',
  'winch-lines',
  'closing-ring',
  'concertina',
  'tally-tags',
  'weigh-anchors',
  'timer-ring',
  'quiet-take',
] as const;
export const CACHE_WORK_STYLES = [
  'keel-squeeze',
  'closing-ring',
  'tractor-beams',
  'ping-and-pull',
  'clock-rim',
  'hex-sweep',
] as const;
export const CURRENT_STYLES = [
  'quiet-drift',
  'streaks',
  'long-flowlines',
  'plankton-motes',
  'stream-atlas',
] as const;
export const CURRENT_HEAT_STYLES = [
  'full-flow',
  'field-and-streaks',
  'fast-water',
  'dusk-field',
  'ripple-chevrons',
] as const;
export const POI_STYLES = [
  'chart-abbreviation',
  'sea-bed',
  'dot-and-label',
  'reach',
  'shape-grammar',
] as const;
export const DEPTH_STYLES = [
  'feathered',
  'stepped',
] as const;

/** The languages the game speaks (languages.toml), in contract order.
 * The first tag is the source language. `endonym` is the name of the
 * language in that language: the LANGUAGE row prints it, because a player
 * who cannot read the current language must still find their own. */
export const LANGUAGES = [
  { tag: 'en', endonym: 'English' },
  { tag: 'de', endonym: 'Deutsch' },
] as const;


/** The six theme categories (themes.toml), in the row order of the
 * MODDING tab. A stored setting and a stored custom theme carry the word. */
export const THEME_CATEGORIES = [
  'ui',
  'world',
  'marks',
  'chart',
  'music',
  'sfx',
] as const;
export type ThemeCategory = (typeof THEME_CATEGORIES)[number];

/** The theme code (themes.md §5): THEME_CODE_LEN symbols of this
 * alphabet. A person types it in any case; the server prints it in upper case. */
export const THEME_CODE_ALPHABET = 'BCDFGHJKMNPQRSTVWXYZ23456789';
export const THEME_CODE_LEN = 4;

/** The keys of a UI body (themes.md §3.1): one "#rrggbb" per token, and
 * the two alphas of the token sheet. */
export const THEME_UI_TOKENS = [
  'accent',
  'text',
  'faint',
  'panel',
  'sheet',
  'ink',
  'deep',
] as const;
export const THEME_UI_ALPHAS = [
  'panelAlpha',
  'edgeAlpha',
] as const;

/** The fields of a World and a Marks body (themes.md §3.4, §3.5), in
 * the row order of the theme workshop, each with the ids it may take. */
export const THEME_WORLD_FIELDS = {
  territory: TERRITORY_STYLES,
  terrain: TERRAIN_STYLES,
  currents: CURRENT_STYLES,
  currentsHeat: CURRENT_HEAT_STYLES,
  depth: DEPTH_STYLES,
  factionSite: FACTION_SITE_STYLES,
  factionHull: FACTION_HULL_STYLES,
  factionEdge: FACTION_EDGE_STYLES,
  factionKey: FACTION_KEY_STYLES,
  spawn: SPAWN_STYLES,
  notices: NOTICE_STYLES,
  dock: DOCK_STYLES,
  banner: BANNER_STYLES,
  torpedo: TORPEDO_STYLES,
  detonation: DETONATION_STYLES,
  seaMineDetonation: SEA_MINE_DETONATION_STYLES,
  reload: RELOAD_STYLES,
  damage: DAMAGE_STYLES,
  shotMelee: SHOT_MELEE_STYLES,
  shotArtillery: SHOT_ARTILLERY_STYLES,
  shotSentinel: SHOT_SENTINEL_STYLES,
  hitShield: HIT_SHIELD_STYLES,
  hitHull: HIT_HULL_STYLES,
  hitSink: HIT_SINK_STYLES,
  holdFire: HOLD_FIRE_STYLES,
  beam: BEAM_STYLES,
  aura: AURA_STYLES,
  gather: GATHER_STYLES,
  anchoring: ANCHOR_STYLES,
  seaMineLay: SEA_MINE_LAY_STYLES,
  deployableLay: DEPLOYABLE_LAY_STYLES,
  seaMineCollect: SEA_MINE_COLLECT_STYLES,
  deployableCollect: DEPLOYABLE_COLLECT_STYLES,
  cacheWork: CACHE_WORK_STYLES,
} as const;
export const THEME_MARKS_FIELDS = {
  ship: SHIP_STYLES,
  shipCorsair: HULL_STYLES,
  shipCutlass: HULL_STYLES,
  shipLancer: HULL_STYLES,
  shipFalconet: HULL_STYLES,
  shipBasilisk: HULL_STYLES,
  shipBombard: HULL_STYLES,
  shipCommodore: HULL_STYLES,
  shipVedette: HULL_STYLES,
  shipDredger: HULL_STYLES,
  shipSentinel: HULL_STYLES,
  shipBeacon: HULL_STYLES,
  shipOracle: HULL_STYLES,
  shipTender: HULL_STYLES,
  shipSapper: HULL_STYLES,
  ownFleet: OWN_FLEET_STYLES,
  squadFleet: SQUAD_FLEET_STYLES,
  shield: SHIELD_STYLES,
  digits: DIGIT_STYLES,
  hold: HOLD_SYMBOLS,
  structure: STRUCTURE_SYMBOLS,
  deployable: DEPLOYABLE_STYLES,
  seaMine: SEA_MINE_STYLES,
  ownHold: OWN_HOLD_STYLES,
  squadHold: SQUAD_HOLD_STYLES,
  deposit: DEPOSIT_STYLES,
  mapMarker: MAP_MARKER_STYLES,
  poi: POI_STYLES,
} as const;

/** The subcategories of a World body (themes.md §3.4, R1008), in
 * heading order. THEME_WORLD_GROUP names the group of every World field,
 * and the theme workshop draws one heading over each block of rows. */
export const THEME_WORLD_GROUPS = [
  'map',
  'faction',
  'interface',
  'combat',
  'support',
  'deployables',
] as const;
export type ThemeWorldGroup = (typeof THEME_WORLD_GROUPS)[number];
export const THEME_WORLD_GROUP = {
  territory: 'map',
  terrain: 'map',
  currents: 'map',
  currentsHeat: 'map',
  depth: 'map',
  factionSite: 'faction',
  factionHull: 'faction',
  factionEdge: 'faction',
  factionKey: 'faction',
  spawn: 'interface',
  notices: 'interface',
  dock: 'interface',
  banner: 'interface',
  torpedo: 'combat',
  detonation: 'combat',
  seaMineDetonation: 'combat',
  reload: 'combat',
  damage: 'combat',
  shotMelee: 'combat',
  shotArtillery: 'combat',
  shotSentinel: 'combat',
  hitShield: 'combat',
  hitHull: 'combat',
  hitSink: 'combat',
  holdFire: 'combat',
  beam: 'support',
  aura: 'support',
  gather: 'support',
  anchoring: 'support',
  seaMineLay: 'deployables',
  deployableLay: 'deployables',
  seaMineCollect: 'deployables',
  deployableCollect: 'deployables',
  cacheWork: 'deployables',
} as const;

/** The feature flag of each World and Marks field that draws a part
 * behind a flag (themes.toml `flag`, v2-the-deep.md §11.8). A field this
 * table does not name shows on every page. */
export const THEME_FIELD_FLAG: Readonly<Record<string, string>> = {
  currents: 'currents',
  currentsHeat: 'currents',
  depth: 'depth',
  seaMineDetonation: 'sapper',
  seaMineLay: 'sapper',
  deployableLay: 'sapper',
  seaMineCollect: 'sapper',
  deployableCollect: 'sapper',
  cacheWork: 'treasure',
  shipSapper: 'sapper',
  deployable: 'sapper',
  seaMine: 'sapper',
  poi: 'discovery',
};

/** The bounds of a custom theme body (ops.toml, themes.md §3, §11.3):
 * the canonical JSON size, the tracks of a Music body, the clips per effect
 * id of an SFX body and the voice delay in milliseconds. */
export const THEME_MAX_BYTES = 16384;
export const THEME_MAX_TRACKS = 200;
export const THEME_MAX_VARIANTS = 8;
export const THEME_MAX_DELAY_MS = 5000;

/** The sfx vocabulary in the groups of audio.toml, in document order
 * (themes.md §3.3). The theme workshop draws one section per group. */
export const SFX_GROUPS = [
  // §4.3 Core set
  [
  'sfx-torpedo-fire',
  'sfx-torpedo-explode',
  'sfx-fleet-fight',
  'sfx-fleet-sink',
  'sfx-sonar-sweep',
  'sfx-scan',
  'sfx-beacon-anchor',
  'sfx-sentinel-anchor',
  'sfx-tender-anchor',
  'sfx-beacon-pulse',
  'sfx-sov1-taken',
  'sfx-sov2-taken',
  'sfx-queue-complete',
  'sfx-auction-sold',
  'sfx-fleet-returned',
  'sfx-quake-stage1',
  'sfx-quake-stage2',
  'sfx-quake-settle',
],
  // §4.4 Extended set — contacts and combat
  [
  'sfx-contact-new',
  'sfx-contact-lost',
  'sfx-torpedo-incoming',
  'sfx-under-attack',
  'sfx-taking-damage',
  'sfx-fleet-hit',
  'sfx-reload-complete',
  'sfx-we-are-detected',
  'sfx-enemy-destroyed',
  'sfx-torpedo-repair',
  'sfx-guard-kill',
  'sfx-torpor-burst',
  'sfx-ambush-shot',
  'sfx-round-select',
  'sfx-shields-down',
  'sfx-magazine-empty',
  'sfx-signature-order',
  'sfx-signature-ready',
  'sfx-contact-broken',
],
  // §4.4 Extended set — economy
  [
  'sfx-build-queued',
  'sfx-ship-launched',
  'sfx-research-complete',
  'sfx-tech-unlocked',
  'sfx-insufficient-resources',
  'sfx-order-refused',
  'sfx-requirement-missing',
  'sfx-deposit-depleted',
  'sfx-cargo-delivered',
  'sfx-blueprint-acquired',
  'sfx-hull-repaired',
  'sfx-redraft-done',
  'sfx-blueprint-masterwork',
],
  // §4.4 Extended set — orders
  [
  'sfx-fleet-departs',
  'sfx-order-ack',
  'sfx-course-plotted',
  'sfx-fleet-fatigued',
  'sfx-expedition-departed',
  'sfx-expedition-returned',
],
  // §4.4 Extended set — territory and season
  [
  'sfx-territory-lost',
  'sfx-siege-defend',
  'sfx-siege-attack',
  'sfx-object-lost',
  'sfx-object-held',
  'sfx-siege-failed',
  'sfx-beacon-destroyed',
  'sfx-beacon-hostile',
  'sfx-truce-ended',
  'sfx-cataclysm',
  'sfx-map-arrival',
  'sfx-quake-final',
],
  // §4.4 Extended set — captains
  [
  'sfx-captain-recruited',
  'sfx-captain-promoted',
  'sfx-captain-lost',
  'sfx-captain-taken',
],
  // §4.4 Extended set — clan, Honour and the clock
  [
  'sfx-clan-hail',
  'sfx-squad-ping',
  'sfx-honour-awarded',
  'sfx-day-night',
  'sfx-truce-tick',
],
  // §4.4 Extended set — achievements
  [
  'sfx-achievement',
  'sfx-trial-passed',
],
  // §4.8 Arcade set — lobby and countdown
  [
  'sfx-arcade-queue-join',
  'sfx-arcade-queue-leave',
  'sfx-arcade-match-found',
  'sfx-arcade-seat-loaded',
  'sfx-arcade-count',
  'sfx-arcade-fight',
],
  // §4.8 Arcade set — match
  [
  'sfx-arcade-buy',
  'sfx-arcade-spawn',
  'sfx-arcade-base-fire',
  'sfx-arcade-kill',
  'sfx-arcade-sov-lead',
  'sfx-arcade-sov-behind',
  'sfx-arcade-objective',
  'sfx-arcade-hold',
  'sfx-arcade-hold-broken',
  'sfx-arcade-capture-start',
  'sfx-arcade-capture-threat',
  'sfx-arcade-capture-broken',
  'sfx-arcade-step-reset',
  'sfx-arcade-surrender',
  'sfx-arcade-last-minute',
  'sfx-arcade-clock-tick',
  'sfx-arcade-win',
  'sfx-arcade-lose',
  'sfx-arcade-draw',
  'sfx-arcade-verdict',
],
  // §4.9 Casino set
  [
  'sfx-casino-chip',
  'sfx-casino-deal',
  'sfx-casino-wheel',
  'sfx-casino-ball',
  'sfx-casino-reel-stop',
  'sfx-casino-win',
  'sfx-casino-payout',
  'sfx-casino-jackpot',
  'sfx-casino-lose',
  'sfx-casino-your-turn',
  'sfx-casino-seat',
  'sfx-club-payout',
],
] as const;
/** The menu shell's own cues (audio.md §4.7). An SFX body may map them
 * too, and the workshop draws them as the last section. */
export const SFX_MENU_IDS = [
  'sfx-menu-tab',
  'sfx-menu-press',
  'sfx-menu-back',
  'sfx-menu-toggle',
  'sfx-menu-tick',
  'sfx-menu-error',
  'sfx-menu-signin',
  'sfx-menu-launch',
  'sfx-menu-chat-send',
  'sfx-menu-chat-recv',
  'sfx-menu-badge',
  'sfx-menu-rename',
  'sfx-menu-purchase',
] as const;

/** The maintenance window (ops.toml [ops.maintenance], deployment.md §6.1):
 * how often the cover asks `/maintenance.json` whether the gate still stands,
 * the minutes before the window the world announces at, and how long the
 * version-mismatch panel stands before the page reloads itself. */
export const MAINTENANCE_POLL_SEC = 10;
export const MAINTENANCE_WARN_MARKS_MINUTES = [30, 10, 5, 1] as const;
export const MAINTENANCE_RELOAD_AFTER_SEC = 5;
/** The planned end (R1191): the one-second poll burst at zero, the grace
 * before the cover says the work runs long, and the widest random pause
 * before the reload that follows the 404. */
export const MAINTENANCE_OPEN_BURST_SEC = 20;
export const MAINTENANCE_RUNNING_LONG_GRACE_SEC = 15;
export const MAINTENANCE_RELOAD_SPREAD_MS = 1000;

/** What the chart furniture calls a ridge massif, a trench, a bank of
 * deposits and a region of water (names.toml, R548). ORDER IS
 * LOAD-BEARING: the client draws from each pool with a stream seeded on
 * the world id, so a reorder renames every feature of a world. */
export const RIDGE_NAMES = [
  'Drifter Shoal',
  'Sable Reef',
  'The Anvil',
  'Kelp Crown',
  'Marrow Rise',
  'Corr Shoal',
  'Iron Spine',
  'The Pinnacle',
  'Basalt Steps',
  'Verge Reef',
  'Quist Shoal',
  'The Ramparts',
  'Ember Rise',
  'Holt Reef',
  'Cinder Bank',
  'The Molar',
  'Sund Shoal',
  'Grave Rise',
  'Nettle Reef',
  'The Buttress',
] as const;
export const TRENCH_NAMES = [
  'The Sounding',
  'Brine Deep',
  'The Long Furrow',
  'Fathom Gut',
  'The Draught',
  'Kessel Deep',
  'Silent Cut',
  'The Swallow',
  'Ravel Deep',
  'The Cold Seam',
  'Locke Gut',
  'The Gullet',
  'Aster Deep',
  'The Slow Fall',
  'Dunn Cut',
  'The Descent',
  'Vantor Deep',
  'The Black Furrow',
  'Ferro Gut',
  'The Reach Down',
] as const;
export const BANK_NAMES = [
  'Kelp Bank',
  'Salvage Bank',
  'The Larder',
  'Cinder Bank',
  'Thorn Bank',
  'The Pastures',
  'Bloom Bank',
  'Rust Bank',
  'The Shallows',
  'Spore Bank',
  'Ashgrove Bank',
  'The Harvest',
  'Frond Bank',
  'Slag Bank',
  'The Meadows',
  'Tallow Bank',
  'Nettle Bank',
  'The Windrow',
] as const;
export const REGION_NAMES = [
  'The Drifter Reach',
  'The Sisters\' Water',
  'The Long Approach',
  'The Cold Quarter',
  'The Kelp Marches',
  'The Rust Shallows',
  'The Choir Deeps',
  'The Compact Sound',
  'The Middle Water',
  'The Broken Ground',
  'The Silent Reach',
  'The Outer Sound',
  'The Winter Water',
  'The Salt Marches',
  'The Deep Quarter',
  'The Near Approach',
  'The Lost Sound',
  'The Far Reach',
] as const;

/** The first names of the captain pool that a woman carries (names.toml,
 * R1180). The captain portrait reads the sex off the first name, so the
 * face never contradicts the name; a name not on the list draws a man. */
export const CAPTAIN_FIRST_FEMALE = [
  'Ilse',
  'Vashti',
  'Ndidi',
  'Rhea',
  'Yuki',
  'Mira',
  'Selin',
  'Juno',
  'Cassia',
  'Nadia',
] as const;

/** The current gate and the lair home of the point of interest marks
 * (world.toml [world.poi], the first row of npc.toml [[npc.monsters]], mockup round 114):
 * the gate multiplier, the gate reach in hex steps and the home radius of
 * a monster round its lair, in world px. A compile-time copy of the pack,
 * the fallback of a page with no world hello (R2714):
 * `render/poi-rules.ts` reads it. */
export const POI_GATE_MUL = 2;
export const POI_GATE_REACH_CELLS = 3;
export const POI_LAIR_HOME_PX = 60;
